// =====================================================================
//  CONFIGURATION SUPABASE  —  À REMPLIR (une seule fois)
// =====================================================================
//  Où trouver ces valeurs :
//  Supabase Dashboard  ->  ton projet  ->  Project Settings  ->  API
//    - "Project URL"                    = SUPABASE_URL
//    - "anon public" (clé publishable)  = SUPABASE_ANON_KEY
//
//  La clé "anon" est PUBLIQUE par conception : elle peut sans risque
//  se trouver dans le code d'un site. La sécurité viendra plus tard des
//  règles d'accès (RLS) côté base de données. Ne mets JAMAIS ici la clé
//  "service_role" (secrète), elle, ne doit jamais apparaître côté site.
// =====================================================================

// Une page hors compte (connexion, création, confirmation) se déclare par `<html data-hors-compte>` (2.32.47).
// ⚠️ EN TÊTE DE FICHIER : `degradeActuel()` la lit dès le premier rendu, bien avant sa place logique.
const GF_HORS_COMPTE = (() => { try { return document.documentElement.hasAttribute("data-hors-compte"); } catch (e) { return false; } })();
const SUPABASE_URL = "https://kxuorzhlejxzvlkriaoo.supabase.co";
const SUPABASE_ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imt4dW9yemhsZWp4enZsa3JpYW9vIiwicm9sZSI6ImFub24iLCJpYXQiOjE3ODQzODcwMzYsImV4cCI6MjA5OTk2MzAzNn0.ZiU0rT4XMvmbg_9-NNP9XRvrsEfoUltXVTIne9xHg9w";

// ⭐ ADRESSE RÉELLE DU SITE (2026-08-17). `new URL(..., window.location.href)` construit un lien juste
// SUR LE SITE, mais dans l'APK la page tourne sur `https://localhost` — une adresse qui n'existe que
// sur CE téléphone. Un lien « Partager mon profil » envoyé depuis l'APK était donc inutilisable pour
// la personne qui le recevait. `solvea.link` : domaine personnalisé acheté et rattaché au Worker
// (Cloudflare, ~7 $/an) — plus court et plus présentable que l'adresse `.workers.dev` gratuite qui
// servait avant. Un seul endroit à changer si ça bouge un jour.
const SITE_URL_NATIF = "https://solvea.link";

// ⭐⭐ UN LIEN QUI MARCHE DEPUIS L'APK AUSSI (2026-08-20).
//
// 🔴 LE DÉFAUT, ET IL ÉTAIT INVISIBLE TANT QU'AUCUN E-MAIL NE PARTAIT. Les liens de confirmation
// étaient construits avec `new URL("confirmation.html", window.location.href)`. Sur le site, parfait.
// **Dans l'APK, la page tourne sur `https://localhost`** : le lien reçu par e-mail pointait donc sur
// une adresse qui n'existe que sur CE téléphone-là. Ouvert depuis la boîte mail — c'est-à-dire depuis
// n'importe où ailleurs — il ne menait nulle part.
//
// 📌 C'est EXACTEMENT le défaut que `SITE_URL_NATIF` avait été créé pour régler en 2.12.11, sur le lien
// « Partager mon profil ». La cause était la même, la correction aussi, mais elle n'avait été appliquée
// qu'à cet endroit-là. ⛔ **Tout lien destiné à être ouvert AILLEURS que dans l'app passe par ici** —
// e-mail, partage, QR code. `window.location.href` ne convient QUE pour naviguer dans la page courante.
//
// ⚠️ Il ne s'est jamais vu parce qu'aucun e-mail n'arrivait (pas de SMTP branché). Le jour où ils
// partent, il se serait vu tout de suite — sur tous les comptes créés depuis le téléphone.
function gfLienSite(page){
  const natif = (typeof GF_NATIF !== "undefined") && GF_NATIF;
  return new URL(page, natif ? SITE_URL_NATIF : window.location.href).href;
}

// Création du client Supabase (utilisé par toutes les pages).
// persistSession + autoRefreshToken = on reste connecté après avoir fermé l'app.
//
// Deux différences dans l'APK Android (GF_NATIF, défini par natif.js chargé juste avant) :
//  - detectSessionInUrl : sur le site, Supabase lit les jetons dans l'URL au retour de Google.
//    Dans l'app il n'y a pas d'URL de retour — c'est le deep link qui les apporte, donc on coupe.
//  - flowType "pkce" : le flux implicite renvoie les jetons dans le fragment (#), que certaines
//    Custom Tabs ne transmettent pas au deep link. PKCE renvoie un simple ?code=, toujours transmis.
// ⭐⭐ LA CLÉ DE RANGEMENT DE LA SESSION, ÉCRITE NOIR SUR BLANC (2026-08-20).
//
// C'est EXACTEMENT la valeur que la bibliothèque calculait déjà toute seule
// (`sb-${hôte.split(".")[0]}-auth-token`) : l'écrire ici ne déplace donc AUCUNE session existante —
// personne n'est déconnecté par ce changement. Ce qu'elle apporte, c'est le droit de s'en servir :
// « mettre un compte de côté » a besoin d'oublier la session LOCALEMENT, sans prévenir le serveur
// (voir `gfOublierSessionLocale` juste en dessous), et on ne retire pas une clé dont on devine le nom.
// ⛔ Ne jamais la changer : une autre valeur ferait repartir de zéro tous les téléphones déjà connectés.
const GF_SESSION_KEY = "sb-kxuorzhlejxzvlkriaoo-auth-token";

const sb = supabase.createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: !GF_NATIF,
    flowType: GF_NATIF ? "pkce" : "implicit",
    storageKey: GF_SESSION_KEY,
    storage: window.localStorage
  }
});

// ⭐⭐⭐ METTRE UN COMPTE DE CÔTÉ N'EST PAS SE DÉCONNECTER (2026-08-20, son signalement, images 1 et 2 :
// « lorsque j'essaie de reprendre mon compte qui est déjà connecté, ça me dit que ma session a
// expiré »). IL AVAIT RAISON, ET LA CAUSE N'ÉTAIT PAS DANS LE REGISTRE.
//
// 🔴 CE QUE FAIT VRAIMENT `signOut({scope:"local"})`, ET CE N'EST PAS CE QUE SON NOM LAISSE CROIRE :
// il appelle le serveur (`POST /logout?scope=local`), qui **révoque la session courante** — donc le
// jeton de rafraîchissement. « local » ne veut pas dire « sans toucher au serveur », il veut dire
// « cette session-ci plutôt que toutes ». Vérifié dans le code livré de la bibliothèque
// (`@supabase/auth-js`, `_signOut` → `this.admin.signOut(accessToken, scope)`), pas supposé.
//
// Conséquence : « Retour à la page de connexion » inscrivait le compte au registre avec des jetons
// FRAIS… puis les tuait à la ligne suivante. `setSession` ne pouvait alors plus rien reprendre, et
// l'écran annonçait « Session expirée » — sur un compte dont la session venait d'être détruite par
// l'application elle-même, à la demande de personne. Aucune photo périmée là-dedans : le registre
// était juste, c'est le serveur qui n'en voulait plus.
// 📌 Personne ne l'avait vu parce que rien de ce qui touche aux jetons n'est vérifiable sans un vrai
// compte Supabase (HANDOFF § 7) — et le nom de l'option racontait le contraire du code.
//
// ⭐ CE QU'ON FAIT À LA PLACE : mettre un compte de côté OUBLIE la session sur cet appareil, et n'en
// informe pas le serveur. La session reste valable, donc reprenable. C'est le geste des applications
// multi-comptes : on range une session, on ne la détruit pas.
// ⛔ Se DÉCONNECTER, lui, garde `signOut({scope:"local"})` : là, la révocation est le but même du
// bouton. Les deux gestes ne sont pas le même, et c'est précisément ce que ce code sépare.
function gfOublierSessionLocale(){
  try { localStorage.removeItem(GF_SESSION_KEY); } catch (e) {}
}

// ---- LE REGISTRE DES COMPTES DE CET APPAREIL ----------------------------------------------------
// ⭐⭐ 2026-08-16, son signalement : « lorsque je reprends mon compte, cela me demande de me
// connecter avec Google et je dois remettre un mot de passe ».
//
// LA CAUSE, et elle tient en une phrase : `gf_comptes_parques` ne gardait qu'une PHOTO des jetons,
// prise à la seconde où l'on mettait le compte de côté. Or Supabase fait TOURNER le jeton de
// rafraîchissement — un jeton sert une fois, puis il est remplacé. La photo vieillissait donc dans
// son coin, et le jour où l'on voulait reprendre le compte, elle ne valait plus rien : `setSession`
// échouait, et l'application renvoyait vers Google ou vers le mot de passe. Exactement ce qu'il a vu.
//
// LA CORRECTION, celle que font toutes les applications multi-comptes : le registre SUIT la session
// vivante au lieu d'en garder une photo. Supabase prévient à chaque rotation (`TOKEN_REFRESHED`,
// plusieurs fois par jour tant que l'app est ouverte) ; on réécrit l'entrée avec les jetons frais.
// Elle n'a plus le temps de périmer.
//
// ⚠️ Ce registre ne quitte JAMAIS l'appareil : `localStorage`, jamais synchronisé, jamais envoyé
// nulle part. Il est effacé compte par compte à la déconnexion (profil.html).
// ⚠️ Il vit ici, dans `config.js`, et non dans une page : les trois pages (index, app, profil)
// doivent le tenir à jour, sinon celle qui l'oublie laisse de nouveau vieillir la photo.
const GF_COMPTES_KEY = "gf_comptes_parques";
function gfListerComptes(){ try { return JSON.parse(localStorage.getItem(GF_COMPTES_KEY) || "[]"); } catch(e){ return []; } }
function gfSauverComptes(liste){ try { localStorage.setItem(GF_COMPTES_KEY, JSON.stringify(liste)); } catch(e){} }
function gfEnregistrerSession(session){
  if (!session || !session.user || !session.access_token || !session.refresh_token) return;
  const email = session.user.email || "";
  if (!email) return;
  const liste = gfListerComptes().filter(c => c.email !== email);
  // En tête : le plus récemment utilisé d'abord, comme avant. L'ancien marqueur `expire` disparaît
  // au passage — c'est le but, cette entrée-là vient d'une session qui marche.
  liste.unshift({
    email: email,
    username: (session.user.user_metadata && session.user.user_metadata.username) || "",
    provider: (session.user.app_metadata && session.user.app_metadata.provider) || null,
    // ⭐ LA LISTE COMPLÈTE, ET PAS SEULEMENT LE FOURNISSEUR PRINCIPAL (2026-08-16). Depuis qu'un
    // compte peut être LIÉ à Google (profil.html → Sécurité → Comptes liés), `provider` seul ment :
    // il ne nomme qu'une des deux portes. Un compte né chez Google et lié à un mot de passe se
    // faisait donc encore renvoyer vers Google, alors qu'il avait désormais le choix.
    providers: (session.user.app_metadata && session.user.app_metadata.providers) || null,
    access_token: session.access_token,
    refresh_token: session.refresh_token
  });
  gfSauverComptes(liste);
}
// ⚠️ Le `typeof` n'est pas de la prudence décorative : le banc navigateur et les tests remplacent la
// bibliothèque Supabase par un bouchon qui ne connaît que les méthodes dont ils se servent. Sans ce
// garde-fou, `config.js` lève à la première ligne et TOUTES les pages meurent en silence.
if (sb && sb.auth && typeof sb.auth.onAuthStateChange === "function") {
  sb.auth.onAuthStateChange((evenement, session) => {
    // `SIGNED_OUT` n'est pas dans la liste, et c'est voulu : la déconnexion retire l'entrée elle-même
    // (profil.html), et la réécrire ici la ferait revenir aussitôt.
    if (session && (evenement === "SIGNED_IN" || evenement === "TOKEN_REFRESHED"
                 || evenement === "INITIAL_SESSION" || evenement === "USER_UPDATED")) {
      gfEnregistrerSession(session);
      // ⭐⭐⭐ ET LE TEMPS RÉEL REÇOIT LE NOUVEAU JETON (2.32.11, son journal du 2026-09-17 :
      // « social-temps-reel : CHANNEL_ERROR (toujours coupé après 30 s) », encore et encore).
      //
      // 🔴 CE QUI SE PASSAIT, ET L'ANALYSE D'AVANT ÉTAIT FAUSSE. Le 2026-09-01, j'avais conclu
      // « c'est un websocket qui tombe sur un téléphone, supabase-js se rebranche tout seul » — et
      // j'avais vérifié en base que `public.messages` est bien publiée en temps réel (elle l'est,
      // revérifié le 2026-09-17, avec `foyers` et `foyer_membres`). Sauf que SA LIGNE DIT LE
      // CONTRAIRE : « toujours coupé après 30 s » ne s'écrit QUE si le canal n'est pas revenu au
      // bout de 30 secondes. Une coupure réseau se répare en deux ou trois ; celle-ci, non.
      //
      // ⛔ LA VRAIE CAUSE : la socket du temps réel garde le jeton qu'elle avait à sa création. Le
      // jeton d'une session dure UNE HEURE ; `supabase-js` le renouvelle tout seul côté REST, mais
      // il ne le repasse PAS à la socket. Passé une heure d'application ouverte, le serveur reçoit
      // donc un jeton périmé et refuse les abonnements protégés par RLS — et comme le jeton ne
      // redeviendra jamais valide, aucune reconnexion ne peut réussir. C'est exactement le profil de
      // ses lignes : rien pendant un moment, puis des erreurs qui s'installent.
      // 📌 Ses horodatages le disaient : 17 h 30, puis 18 h 57 → 19 h 39 (deux fois), des plages
      // longues, pas des pics de tunnel.
      //
      // ⚠️ `typeof` COMME PARTOUT DANS CE FICHIER : le bouchon Supabase des bancs ne connaît que les
      // méthodes dont ils se servent. Sans ce garde-fou, `config.js` lèverait à la première ligne et
      // TOUTES les pages mourraient en silence — c'est déjà la raison du `typeof` juste au-dessus.
      try {
        if (sb.realtime && typeof sb.realtime.setAuth === "function") sb.realtime.setAuth(session.access_token);
      } catch (e) {}
    }
  });
}

// ⭐⭐ UN MOT DE PASSE DÉJÀ VOLÉ AILLEURS EST REFUSÉ (2026-10-15, sa décision). La protection de Supabase (« leaked password
// protection ») est réservée à l'offre Pro : refusée par l'API le 2026-10-15. Version gratuite, côté application, avec le
// MÊME service : Have I Been Pwned, par plage (« k-anonymat »). ⛔ Le mot de passe ne part JAMAIS, même chiffré : seuls
// les 5 premiers caractères de son empreinte SHA-1 sont envoyés ; le service répond toutes les empreintes qui commencent
// ainsi (des centaines, complétées de leurres par `Add-Padding`), et la comparaison se fait ICI.
// ⚠️ Si le service ne répond pas (hors ligne, 3 s), on LAISSE PASSER : une vérification de confort ne doit jamais
// empêcher de créer un compte. Contournable par qui modifie l'application — c'est la limite assumée de la version gratuite.
// Appelée aux quatre endroits où l'on choisit un mot de passe : inscription, mot de passe oublié, changement, ajout d'un
// e-mail à un compte Google. Garde : tests/mdp-vole.test.js.
const GF_MSG_MDP_VOLE = "Ce mot de passe circule déjà dans des fuites de données connues : choisissez-en un autre.";
async function gfMotDePasseVole(mdp) {
  try {
    if (!mdp || !(window.crypto && crypto.subtle)) return false;
    const empreinte = await crypto.subtle.digest("SHA-1", new TextEncoder().encode(mdp));
    const hex = Array.from(new Uint8Array(empreinte)).map((o) => o.toString(16).padStart(2, "0")).join("").toUpperCase();
    const arret = new AbortController();
    const minuteur = setTimeout(() => arret.abort(), 3000);
    let r;
    try { r = await fetch("https://api.pwnedpasswords.com/range/" + hex.slice(0, 5), { headers: { "Add-Padding": "true" }, signal: arret.signal }); }
    finally { clearTimeout(minuteur); }
    if (!r || !r.ok) return false;
    const suite = hex.slice(5);
    return (await r.text()).split("\n").some((l) => {
      const [s, n] = l.trim().split(":");
      return s === suite && Number(n) > 0;
    });
  } catch (e) { return false; }
}

// Traduit les messages d'erreur techniques de Supabase en français clair.
function messageErreur(e) {
  const m = (e && e.message) || "";
  const table = {
    "Invalid login credentials": "Email ou mot de passe incorrect.",
    "Email not confirmed": "Votre email n'est pas encore confirmé. Vérifiez votre boîte mail.",
    "User already registered": "Un compte existe déjà avec cet email.",
    "Password should be at least 6 characters": "Le mot de passe doit faire au moins 6 caractères.",
    "Unable to validate email address: invalid format": "Adresse email invalide.",
    // ⭐⭐ « Error sending confirmation email » (2.32.13, sa remontée du 2026-09-17 : « j'ai mis une
    // adresse mail qui n'existe pas ; au lieu de mettre "Error sending confirmation email", il
    // faudrait un message du genre que la personne doit savoir que l'adresse n'existe pas, qu'il faut
    // qu'elle vérifie la saisie de son mail »).
    // 🔴 C'est ce que rend le serveur quand le courriel REBONDIT : le domaine n'existe pas, la boîte
    // est fermée, ou l'adresse est mal tapée. Le message brut était en anglais, technique, et
    // n'indiquait rien à faire — devant un écran de création de compte, c'est le pire moment pour ça.
    // ⚠️ ON NE DIT PAS « cette adresse n'existe pas » : personne ne peut l'affirmer (une boîte peut
    // refuser temporairement). On dit ce qui s'est passé et ce qu'il y a à vérifier.
    "Error sending confirmation email": "Nous n'avons pas pu envoyer l'e-mail à cette adresse. Vérifiez qu'elle est bien écrite (le nom, l'arobase et ce qui suit), puis réessayez.",
    "Error sending recovery email": "Nous n'avons pas pu envoyer l'e-mail à cette adresse. Vérifiez qu'elle est bien écrite, puis réessayez.",
    "Error sending magic link email": "Nous n'avons pas pu envoyer l'e-mail à cette adresse. Vérifiez qu'elle est bien écrite, puis réessayez.",
    "Signups not allowed for this instance": "Les inscriptions sont désactivées.",
    "Email rate limit exceeded": "Trop de tentatives, réessayez dans quelques minutes.",
    "For security purposes, you can only request this after 60 seconds.": "Patientez une minute avant de réessayer.",
    "Invalid TOTP code entered": "Code incorrect, réessayez.",
    // ⭐ 2026-08-20, son signalement (image 5) : « Auth session missing! » s'affichait TEL QUEL, en
    // anglais, en bas de l'écran. C'est ce que rend la bibliothèque quand la session a disparu du
    // stockage — après une révocation côté serveur, par exemple (changement de mot de passe par
    // l'administration : Supabase déconnecte alors TOUTES les sessions du compte). Le message brut
    // ne dit ni ce qui s'est passé, ni quoi faire.
    "Auth session missing!": "Votre session s'est terminée. Reconnectez-vous, puis réessayez.",
    "Session from session_id claim in JWT does not exist": "Votre session s'est terminée. Reconnectez-vous, puis réessayez.",
    // ⭐⭐ LES MESSAGES DU CHANGEMENT DE MOT DE PASSE (2026-08-21). Ils sortaient EN ANGLAIS, tels
    // quels — « Current password required when setting new password. » est celui de son image 6.
    //
    // 📌 ET CE PROJET A LONGTEMPS CONFONDU LES DEUX PREMIERS. Relevé dans la SOURCE du serveur
    // (`supabase/auth`, `internal/api/user.go` — lu, pas supposé ; `*.supabase.co` est bloqué par la
    // politique réseau, mais github ne l'est pas), ce sont DEUX options distinctes :
    //   · `UpdatePasswordRequireReauthentication` (« Secure password change » du tableau de bord)
    //     rend « Password update requires reauthentication », et un `nonce` la satisfait ;
    //   · `UpdatePasswordRequireCurrentPassword` rend « Current password required when setting new
    //     password. », et AUCUN nonce ne la satisfait — il faut le mot de passe actuel, ou une
    //     session de RÉCUPÉRATION (`if !session.IsRecovery()`, le seul cas où le serveur saute le
    //     contrôle). C'est exactement ce que fait « Mot de passe oublié » de la page de connexion.
    // Si l'un des deux reparaît un jour, la phrase française dit maintenant quoi faire, au lieu de
    // laisser un message serveur brut au milieu d'une application en français.
    "Current password required when setting new password.": "Ce compte demande le mot de passe actuel pour en poser un nouveau. Passez par « Mot de passe oublié » sur la page de connexion : il vous en dispense.",
    "Password update requires reauthentication": "Demandez d'abord le code par e-mail, puis saisissez-le avant le nouveau mot de passe.",
    "New password should be different from the old password.": "Le nouveau mot de passe doit être différent de l'ancien.",
    "Token has expired or is invalid": "Ce code n'est plus valable. Demandez-en un nouveau.",
  };
  if (table[m]) return table[m];
  // ⛔ UN REPLI PAR FAMILLE, PARCE QUE LA CORRESPONDANCE EXACTE EST FRAGILE (2.32.13). Le serveur
  // écrit parfois « Error sending confirmation email », parfois avec un détail à la suite : une
  // table à correspondance exacte laisse alors repasser l'anglais brut, et c'est précisément ce
  // qu'il a vu le 2026-09-17. Tout ce qui commence par « Error sending … email » veut dire la même
  // chose pour la personne devant l'écran : le courriel n'est pas parti.
  if (/^Error sending\b/i.test(m))
    return "Nous n'avons pas pu envoyer l'e-mail à cette adresse. Vérifiez qu'elle est bien écrite (le nom, l'arobase et ce qui suit), puis réessayez.";
  return m || "Une erreur est survenue.";
}

// ⭐⭐ LE VRAI MESSAGE D'ERREUR D'UNE FONCTION EDGE (2026-08-19, son signalement, image 5 : « je me
// suis trompé de mot de passe, affiche une notification d'erreur de mot de passe, pas le msg edge
// function je sais pas quoi »).
//
// 🔴 POURQUOI ÇA ARRIVE, ET ÇA VAUT POUR TOUS LES APPELS : quand une fonction Edge répond autre chose
// qu'un 2xx, `supabase-js` ne lit PAS le corps de la réponse. Il rend un `error` dont le message est
// toujours le même — « Edge Function returned a non-2xx status code » — et `data` vaut `null`. Le
// message utile (« E-mail ou mot de passe incorrect pour ce compte. ») est pourtant bien là, dans le
// corps JSON que le serveur a pris la peine d'écrire.
//
// ⭐ `error.context` EST la `Response` : on la relit. C'est le seul moyen d'afficher ce que le serveur
// a réellement dit, et c'est à faire pour CHAQUE `invoke`.
//
// 📌 POURQUOI ICI, ET PAS DANS LE MORCEAU QUI S'EN SERT : `messageErreur` est juste au-dessus, trois
// morceaux d'app.js appellent des fonctions Edge, et confirmation.html en appelle une aussi sans
// charger app.js. Une deuxième copie ailleurs, c'est la garantie qu'un jour l'une des deux traduira
// et pas l'autre — l'incohérence exacte qu'il nous a demandé d'arrêter de produire.
async function messageFonctionEdge(error, data) {
  // 🔴🔴 DEUX CLÉS, ET C'EST TOUT LE DÉFAUT DU 2026-08-21 (son image 6, « Le serveur n'a pas répondu
  // (erreur 400) » alors que le serveur avait écrit une phrase parfaitement claire).
  //
  // Cette fonction ne lisait que `error`. Or les fonctions Edge de ce dépôt ne parlent pas toutes le
  // même dialecte : `confirmer-adresse-envoi`, `envoyer-mes-donnees` et `retirer-identite` répondent
  // `{ok:false, msg:"…"}`, les autres `{error:"…"}`. Pour ces TROIS-LÀ, n'importe quel refus — code
  // faux, adresse invalide, dernière identité — s'affichait donc en « Le serveur n'a pas répondu »,
  // c'est-à-dire « l'application est cassée » pour qui lit l'écran. Il réessaie au lieu de corriger
  // sa saisie, et c'est EXACTEMENT le reproche du 2026-08-19 (son image 5), à moitié corrigé.
  //
  // ⛔ On ne renomme pas les clés côté serveur pour « harmoniser » : une fonction Edge se redéploie à
  // la main, le client part par un push, et il existerait une fenêtre où le client ne comprend plus
  // rien. C'est le LECTEUR qui accepte les deux (règle B1).
  // ⚠️ `msg` d'abord, car c'est le seul des deux qui soit toujours destiné à un humain ; certaines
  // fonctions mettent dans `error` un code technique.
  const lisible = (o) => (o && typeof o === "object" && (o.msg || o.error)) || "";
  if (lisible(data)) return lisible(data);
  try {
    if (error && error.context && typeof error.context.json === "function") {
      const corps = await error.context.json();
      if (lisible(corps)) return lisible(corps);
    }
  } catch (e) { /* corps illisible (HTML d'une passerelle, réponse vide) : on retombe plus bas */ }
  // ⛔ LE DERNIER FILET, ET IL EST INDISPENSABLE. Si le corps est illisible — une passerelle qui rend
  // du HTML, un 502, une réponse vide — `messageErreur()` n'a rien à traduire et laisserait passer la
  // phrase anglaise telle quelle. C'est précisément ce qu'il ne veut plus voir. On la remplace donc par
  // ce qu'elle veut dire pour quelqu'un qui n'écrit pas le code : le serveur n'a pas répondu.
  const brut = String((error && error.message) || "");
  if (/non-2xx|failed to send a request|edge function/i.test(brut)) {
    const statut = (error && error.context && error.context.status) || 0;
    if (statut === 404) return "Cette fonctionnalité n'est pas encore disponible sur le serveur. Rien n'a été modifié.";
    if (statut === 401 || statut === 403) return "Vous n'êtes plus authentifié. Reconnectez-vous, puis réessayez.";
    return "Le serveur n'a pas répondu" + (statut ? " (erreur " + statut + ")" : "") + ". Réessayez dans un instant.";
  }
  return messageErreur(error);
}

// Petit garde-fou : renvoie vers la page de connexion si non connecté.
async function exigerConnexion() {
  const { data } = await sb.auth.getSession();
  if (!data.session) {
    window.location.href = "index.html#connexion";
    return null;
  }
  const ok = await exigerMFA();
  if (!ok) return null;
  return data.session;
}

// Si la 2FA est activée sur le compte mais que la session n'a pas encore validé le
// 2e facteur (cas typique : connexion Google, qui ne redemande jamais le code seule),
// on bloque l'accès avec un écran de code tant que ce n'est pas vérifié.
async function exigerMFA() {
  const { data: aal } = await sb.auth.mfa.getAuthenticatorAssuranceLevel();
  if (!(aal && aal.currentLevel === "aal1" && aal.nextLevel === "aal2")) return true;
  return new Promise(resolve => {
    const ov = document.createElement("div");
    ov.style.cssText = "position:fixed;inset:0;z-index:99999;background:var(--bg);display:flex;flex-direction:column;align-items:center;justify-content:center;padding:20px;";
    ov.innerHTML =
      '<div style="font-size:40px;margin-bottom:8px;">🔐</div>' +
      '<p style="font-weight:600;font-size:16px;margin:0 0 4px;color:var(--text);">Double authentification</p>' +
      '<p style="color:var(--muted);font-size:13px;margin:0 0 16px;text-align:center;max-width:280px;">Entrez le code de votre application d\'authentification pour continuer.</p>' +
      '<input id="mfa-gate-code" class="champ" type="text" inputmode="numeric" autocomplete="one-time-code" placeholder="123456" style="max-width:220px;text-align:center;letter-spacing:4px;font-size:18px;" />' +
      '<div id="mfa-gate-msg" style="min-height:20px;"></div>' +
      '<button id="mfa-gate-valider" class="btn btn-plein" style="max-width:220px;margin-top:4px;">Valider</button>' +
      '<button id="mfa-gate-annuler" class="lien">Se déconnecter</button>';
    document.body.appendChild(ov);
    const input = ov.querySelector("#mfa-gate-code");
    const msgEl = ov.querySelector("#mfa-gate-msg");
    input.focus();
    // ⛔ GARDE ANTI DOUBLE-SOUMISSION (2026-08-23, journal technique : "Failed to execute
    // 'removeChild'... The node to be removed is not a child of this node"). Sans elle, Entrée
    // ET un clic sur "Valider" (ou un double-tap) avant la fin du premier appel réseau lancent
    // valider() deux fois en parallèle : le premier retire `ov` du DOM, le second — resté en vol
    // sur son propre `await` — tente de le retirer une seconde fois et échoue.
    let enCours = false;
    async function valider() {
      if (enCours) return;
      const code = input.value.trim();
      if (!code) return;
      enCours = true;
      msgEl.textContent = "Vérification…"; msgEl.style.color = "var(--muted)";
      const { data: facteurs } = await sb.auth.mfa.listFactors();
      const totp = facteurs && facteurs.totp && facteurs.totp.find(f => f.status === "verified");
      if (!totp) { document.body.removeChild(ov); resolve(true); return; }
      const { data: ch, error: e1 } = await sb.auth.mfa.challenge({ factorId: totp.id });
      if (e1) { msgEl.textContent = messageErreur(e1); msgEl.style.color = "var(--danger)"; enCours = false; return; }
      const { error: e2 } = await sb.auth.mfa.verify({ factorId: totp.id, challengeId: ch.id, code });
      if (e2) { msgEl.textContent = "Code incorrect, réessayez."; msgEl.style.color = "var(--danger)"; input.value = ""; input.focus(); enCours = false; return; }
      document.body.removeChild(ov);
      resolve(true);
    }
    ov.querySelector("#mfa-gate-valider").onclick = valider;
    input.addEventListener("keydown", e => { if (e.key === "Enter") valider(); });
    // ⚠️ `scope: "local"` — même raison qu'à la déconnexion normale (profil.html) : sans lui, Supabase
    // révoque TOUTES les sessions du compte, sur TOUS les appareils, et emporte au passage les jetons des
    // comptes mis de côté sur celui-ci. Abandonner un écran de code n'est pas une déconnexion globale.
    ov.querySelector("#mfa-gate-annuler").onclick = async () => { await sb.auth.signOut({ scope: "local" }); window.location.href = "index.html#connexion"; };
  });
}

// ---- Thèmes de fond (partagé sur toutes les pages) ----
// ⚠️ LES THÈMES DE LA BOUTIQUE SONT DANS CETTE LISTE (2026-08-13). Sans eux, `appliquerThemeNomme`
// ne RETIRE pas leur classe en changeant de thème : on empilerait Crépuscule et Forêt sur <html>,
// et le dernier déclaré gagnerait pour toujours. Toute nouvelle couleur s'ajoute ici, point.
// ⚠️ Gardée pour `tests/boutique.test.js`, qui vérifie qu'un thème vendu est déclaré partout.
// La vraie liste de travail est maintenant FONDS / THEMES, plus bas — deux axes depuis le 2026-08-14.
const THEMES_FOND = ["clair","sombre","rose","claude","brume","papier","crepuscule","abysse","foret","terracotta"];

// ⭐⭐⭐ LE CATALOGUE DES CONTOURS DE PHOTO — DECLARE ICI ET NULLE PART AILLEURS (2026-08-13).
//
// 🔴 POURQUOI IL A DEMENAGE DANS CONFIG.JS. Sa demande : « cette option doit se retrouver dans le
// profil plutot, deplace ». Le profil est `profil.html`, une PAGE SEPAREE qui ne charge pas app.js.
// Recopier le catalogue la-bas aurait cree deux listes a tenir d'accord — exactement le piege que
// ce depot documente partout ailleurs (les icones de lanceur ont coute un test de 5 fichiers pour
// cette raison). `config.js` est deja charge par les deux pages : c'est le seul endroit commun.
//
// ⚠️ LES IDENTIFIANTS NE SE RENOMMENT JAMAIS : ils sont la ligne d'achat de quelqu'un, en base.
// ⚠️ Les regles CSS correspondantes vivent dans `styles.css` (charge par les deux pages aussi), et
// PAS dans app.css. `tests/boutique.test.js` verifie qu'aucune n'est oubliee.
// ⭐⭐⭐ VINGT-DEUX CONTOURS, RANGÉS PAR FAMILLE (2026-09-06, sa demande, image 7 : « rajoute
// beaucoup + de contours s'il te plaît »). Il y en avait dix ; il en voulait « une vingtaine, avec
// des familles ».
//
// 📌 CE QU'UN CONTOUR PEUT ÊTRE, ET RIEN D'AUTRE : un `box-shadow` posé sur un avatar de 34 px.
// Des anneaux concentriques, et un halo. Pas de dégradé animé, pas d'image — vérifié dans le code
// avant d'écrire cette liste, pas supposé. Les quatre familles exploitent donc les trois seules
// variations disponibles :
//   · PIERRES  — deux anneaux, un sombre puis un vif : la taille d'une gemme ;
//   · MÉTAUX   — deux anneaux proches en valeur, sans halo : une surface qui réfléchit ;
//   · NATURE   — un seul anneau, franc, sans halo : une couleur de matière ;
//   · LUMIÈRES — un anneau et un HALO : quelque chose qui émet.
// ⛔ Ces quatre recettes ne sont pas décoratives : c'est ce qui permet de reconnaître une famille
// sans lire son nom. Un contour ajouté doit suivre celle de sa famille.
//
// ⚠️ TROIS ENDROITS PAR CONTOUR, ET `tests/boutique.test.js` REFUSE L'OUBLI DES DEUX PREMIERS :
//   ① ici (`GF_CONTOURS`), ② `styles.css` (`.deco-X .avatar-mini` + la variante `.art-apercu-deco`),
//   ③ `app.css` (`.bq-vignette.deco-X`, la vignette du rayon).
// ⚠️ `famille` A ÉTÉ AJOUTÉ LE 2026-09-06 : les dix premiers n'en avaient pas. Un contour sans
// famille se rangerait dans « Autres » plutôt que de faire planter la boutique — mais il ne doit
// pas y en avoir.
const GF_CONTOURS = [
  // --- PIERRES : deux anneaux, un sombre puis un vif ------------------------------------------
  { id:"deco-rubis",      nom:"Rubis",        prix:30, famille:"Pierres",  dit:"Un rouge profond, deux anneaux." },
  { id:"deco-saphir",     nom:"Saphir",       prix:30, famille:"Pierres",  dit:"Bleu d'encre, cerclé de bleu vif." },
  { id:"deco-emeraude",   nom:"Émeraude",     prix:30, famille:"Pierres",  dit:"Un vert de gemme, franc et net." },
  { id:"deco-amethyste",  nom:"Améthyste",    prix:30, famille:"Pierres",  dit:"Violet sombre et violet clair." },
  { id:"deco-topaze",     nom:"Topaze",       prix:30, famille:"Pierres",  dit:"Or brûlé, cerclé de miel." },
  { id:"deco-onyx",       nom:"Onyx",         prix:30, famille:"Pierres",  dit:"Noir sur noir, à peine un reflet." },
  // --- MÉTAUX : deux anneaux proches en valeur, sans halo --------------------------------------
  { id:"deco-or",         nom:"Or",           prix:30, famille:"Métaux",   dit:"Un liseré doré, double." },
  { id:"deco-cuivre",     nom:"Cuivre",       prix:30, famille:"Métaux",   dit:"Un cercle de cuivre chaud." },
  { id:"deco-argent",     nom:"Argent",       prix:30, famille:"Métaux",   dit:"Gris poli, presque blanc." },
  { id:"deco-bronze",     nom:"Bronze",       prix:30, famille:"Métaux",   dit:"Brun métal, sourd et chaud." },
  { id:"deco-metal",      nom:"Métal Solvea", prix:30, famille:"Métaux",   dit:"L'anneau du logo, autour de vous." },
  // --- NATURE : un seul anneau franc, sans halo ------------------------------------------------
  { id:"deco-laurier",    nom:"Laurier",      prix:30, famille:"Nature",   dit:"Un anneau vert Solvea, discret." },
  { id:"deco-sakura",     nom:"Sakura",       prix:30, famille:"Nature",   dit:"Rose poudré, comme l'icône." },
  { id:"deco-fougere",    nom:"Fougère",      prix:30, famille:"Nature",   dit:"Le vert sombre d'un sous-bois." },
  { id:"deco-givre",      nom:"Givre",        prix:30, famille:"Nature",   dit:"Bleu pâle, comme une vitre au matin." },
  { id:"deco-lagune",     nom:"Lagune",       prix:30, famille:"Nature",   dit:"Un turquoise d'eau peu profonde." },
  // --- LUMIÈRES : un anneau ET un halo ----------------------------------------------------------
  { id:"deco-aurore",     nom:"Aurore",       prix:30, famille:"Lumières", dit:"Un dégradé vert et turquoise." },
  { id:"deco-abysse",     nom:"Abysse",       prix:30, famille:"Lumières", dit:"Violet nuit et halo." },
  { id:"deco-azur",       nom:"Azur",         prix:30, famille:"Lumières", dit:"Un bleu profond, deux anneaux." },
  { id:"deco-ambre",      nom:"Ambre",        prix:30, famille:"Lumières", dit:"Un orange chaud, comme un soleil couchant." },
  { id:"deco-braise",     nom:"Braise",       prix:30, famille:"Lumières", dit:"Rouge vif, et sa chaleur autour." },
  { id:"deco-crepuscule", nom:"Crépuscule",   prix:30, famille:"Lumières", dit:"Prune et rose, à la tombée du jour." },
];
// L'ordre des familles à l'écran. ⛔ Écrit ici et nulle part ailleurs : la boutique et l'écran
// Contours lisent cette liste, ils n'en tiennent pas chacun la sienne.
const GF_CONTOUR_FAMILLES = ["Pierres", "Métaux", "Nature", "Lumières"];
try { window.GF_CONTOURS = GF_CONTOURS; window.GF_CONTOUR_FAMILLES = GF_CONTOUR_FAMILLES; } catch (e) {}

// Les quelques glyphes dont les DEUX pages ont besoin. Meme raison que GF_CONTOURS : `profil.html`
// ne charge pas `app.js`, et recopier un SVG a la main, c'est se condamner a le retoucher deux fois.
// ⚠️ Ce n'est PAS le catalogue d'icones de l'application (ICONES_UI, app.js) : seulement le
// strict necessaire au profil.
const GF_ICONES_UI = {
  contour:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" role="img" aria-label="Contour de photo"><circle cx="12" cy="12" r="9.2"/><circle cx="12" cy="9.7" r="2.9" fill="currentColor" stroke="none"/><path d="M6.6 18.4a5.8 5.8 0 0 1 10.8 0" fill="currentColor" stroke="none"/></svg>'
};
try { window.GF_ICONES_UI = GF_ICONES_UI; } catch (e) {}
// ==================================================================================================
// ⭐⭐⭐ DEUX AXES : LE FOND, ET LE THÈME (2026-08-14, sa demande)
// ==================================================================================================
// Sa phrase : « le thème c'est un thème et le fond d'écran c'est la couleur […] si je choisis rose,
// alors le thème en bas doit garder le sien, mais s'adapter à la couleur choisie ».
//
// Deux classes indépendantes sur <html>, `fond-<x>` et `theme-<y>`, et n'importe quelle combinaison
// des deux. Le CSS est en tête de `styles.css` — tout est expliqué là-bas.
//
// 🔴 LA MIGRATION EST SILENCIEUSE ET SANS PERTE. Avant, une seule clé `gf_theme` portait les deux.
// `migrerApparence()` la découpe UNE fois : l'ancienne valeur devient le FOND, et le THÈME devient
// celui du même nom s'il en existe un (les quatre achetés), sinon « solvea ». Résultat visuel
// identique au pixel près — personne ne voit son app changer d'apparence en se mettant à jour.
// ⭐ TROIS FONDS NEUTRES DE PLUS, OFFERTS (2026-08-31) : Brume, Papier, Graphite. Ils ne se vendent
// pas et n'appartiennent a aucun theme -- ce sont des MATIERES. Ils existent parce que les douze
// identites ajoutees le meme jour n'apportent volontairement AUCUN fond : il fallait de quoi les
// poser. ⚠️ Un fond ajoute ici doit AUSSI rejoindre `FONDS_SOMBRES` s'il est sombre, et la liste
// de clarte correspondante en tete de styles.css -- sinon son accent est choisi a l'envers et
// devient illisible.
// ⛔ LE FOND « NOIR » A ÉTÉ RETIRÉ (2026-09-05, sa décision : « noir, retire-le »). C'était un noir
// pur (#000) qui doublait « Sombre » sans rien apporter, et qui rendait les liserés invisibles.
// ⚠️ Un compte qui l'avait est basculé sur « Sombre » par `migrerApparence` : sans ça, `gf_fond=noir`
// resterait écrit, aucune classe `fond-noir` ne répondrait, et l'écran retomberait EN SILENCE sur le
// fond par défaut — le piège déjà documenté pour les thèmes sans fond.
// ⛔⛔ BEIGE ET GRAPHITE SONT SORTIS LE 2026-09-06 (sa décision, son image 5 : « retire un fond
// d'écran clair et un fond d'écran sombre parmi la liste, c'est moche qu'il y en ait 5 et que tout
// n'est pas aligné »). La vraie raison est plus simple que l'alignement : Beige et Papier étaient
// deux crèmes qu'on ne distingue pas à la vignette, Claude et Graphite deux gris presque noirs.
// Cinq clairs et cinq sombres laissaient en plus une rangée bancale sur une grille de quatre.
// 📌 Il reste QUATRE de chaque, et la grille tombe juste. `migrerApparence()` rattrape les deux clés
// disparues : beige -> papier, graphite -> claude — le voisin le plus proche, pas le fond par défaut.
const FONDS = ["clair","rose","terracotta","brume","papier","sombre","claude","crepuscule","abysse","foret"];
const FONDS_SOMBRES = ["sombre","claude","crepuscule","abysse","foret"];
// ⭐ LES DEUX SEULS FONDS OFFERTS (2026-09-06, son image 1 : « tu dois uniquement laisser les fonds
// d'écran blanc et sombre au-dessus, tout en retirant le reste, car ils sont en double »). Les huit
// autres s'achètent, et n'apparaissent donc QUE dans la rangée générée depuis la boutique.
// ⛔ Cette liste et `FONDS_OFFERTS` dans 16-boutique.js disent la même chose : elles doivent le rester.
const FONDS_GRATUITS = ["clair","sombre"];
// ⭐ DOUZE IDENTITES COLOREES DE PLUS (2026-08-31, sa demande : « ajoutes-en d'autres [...] au moins
// 12 supplementaires »). ⚠️ CONTRAIREMENT AUX QUATRE PREMIERES, ELLES N'APPORTENT PAS DE FOND :
// leur cle n'existe QUE comme `theme-<cle>` dans styles.css, jamais comme `fond-<cle>`. Tout code
// qui pose un fond a partir d'une cle de theme doit donc verifier `FONDS.includes(cle)` avant --
// sans ca, on ecrit "gf_fond=lavande" dans le stockage, aucune classe ne correspond, et l'ecran
// retombe silencieusement sur le fond clair par defaut.
const THEMES = ["solvea","crepuscule","abysse","foret","terracotta",
  "lavande","cerise","safran","indigo","ciel","corail","acier","quartz","emeraude","vigne","moutarde","onyx"];
// Les themes qui apportent AUSSI un fond du meme nom. C'est cette liste, et pas `THEMES`, que lit
// tout ce qui parle des deux axes a la fois.
const THEMES_AVEC_FOND = ["crepuscule","abysse","foret","terracotta"];

// ==================================================================================================
// ⭐⭐⭐ LE CINQUIÈME RAYON : LES DÉGRADÉS (2026-09-07, sa demande)
// ==================================================================================================
// « Ajoute ça dans la boutique et avoir un endroit dédié pour y équiper, dans les fonds d'écran et
// thème, et le dégradé coûtera 200 Nacres Aurales. » Ses quatre réponses, déjà tranchées :
//   · SUR QUOI  → « le fond de l'application entière » ;
//   · QUEL STYLE → des dégradés « très doux, entre deux tons proches » (son choix explicite) ;
//   · AVEC UNE IMAGE PERSO → « le dégradé par-dessus l'image, EN VOILE oui […] si la couleur peut
//     poser problème de lisibilité alors il faut que la personne change de dégradé tout simplement » ;
//   · LA ROULETTE → oui, comme les thèmes et les contours (4 par jour).
//
// 🔴 CE QUI A DÉCIDÉ DE LA FORME : CE SONT DES VOILES, PAS DES FONDS. Chaque dégradé est écrit en
// `rgba(...)` à 26 % — il se pose PAR-DESSUS le fond d'écran (ou par-dessus l'image perso) sans le
// remplacer. Deux conséquences, et les deux sont voulues :
//   ① un fond clair reste clair, un fond sombre reste sombre : aucun dégradé ne peut rendre un écran
//      illisible. Il a accepté le risque (« qu'elle change de dégradé tout simplement ») — ne pas le
//      faire courir quand la même idée tient sans lui est mieux que de s'en tenir à sa permission ;
//   ② les deux axes existants ne bougent pas d'un pixel. Un dégradé n'est ni un fond ni un thème :
//      c'est une lumière posée dessus. C'est exactement la séparation qu'il a demandée le 2026-09-05.
//
// ⛔ LE CSS EST ÉCRIT ICI, ET NULLE PART AILLEURS. Le fond de l'écran, la pastille de Personnalisation,
// la vignette de la boutique et les quatre maquettes de la fiche lisent tous CETTE chaîne. Une seconde
// liste dans styles.css divergerait au premier ajout — c'est le défaut des vignettes de thème, réglé
// le 2026-09-02 exactement de cette façon.
// ⛔⛔⛔ ET UN DÉGRADÉ DOIT SE VOIR COMME UN DÉGRADÉ (2026-09-07, sa question, et elle était méritée :
// « As-tu réellement compris ce qu'est un dégradé ? car tes dégradés on dirait un simple fond
// d'écran, il faut que ce soit un effet dégradé VISUEL, et ça même dans les exemples que tu as
// faits »).
//
// 🔴 CE QUE J'AVAIS ÉCRIT, ET POURQUOI ÇA NE POUVAIT PAS MARCHER. Chaque dégradé était
// `linear-gradient(160deg, rgba(A,.26), rgba(B,.26))` avec A et B presque identiques — pour « Aube »,
// (255,214,196) contre (255,233,209), c'est-à-dire **19 points d'écart sur une seule composante**,
// le tout à 26 % d'opacité. Deux teintes qu'on ne distingue déjà pas à pleine opacité, diluées au
// quart : le résultat est mathématiquement une teinte PLATE. J'avais suivi sa consigne « entre deux
// tons proches » à la lettre, et si loin qu'il ne restait plus de dégradé du tout. Sa consigne
// disait DOUX ; j'ai livré INEXISTANT. Ce n'est pas la même chose, et c'est une erreur de degré,
// celle qu'on ne voit jamais en relisant son propre code.
//
// ✅ CE QUI FAIT QU'UN DÉGRADÉ SE VOIT — et il en faut au moins deux des trois :
//   ① UNE VRAIE COURSE D'OPACITÉ, et c'est le levier principal ici. Le voile part fort dans un angle
//      et **s'éteint complètement** (`alpha: 0`) à l'autre bout. On voit donc la couleur NAÎTRE puis
//      DISPARAÎTRE, ce que l'œil lit immédiatement comme une lumière qui traverse l'écran. Une teinte
//      uniforme à 26 %, elle, se lit comme un filtre posé sur l'objectif — c'est-à-dire comme rien ;
//   ② DEUX SOURCES, DEUX COINS. Une seule nappe d'un bord à l'autre reste plate au milieu. Une lueur
//      RADIALE qui éclot dans un angle, plus une nappe LINÉAIRE qui monte du coin opposé, donnent une
//      diagonale ET un creux au centre : c'est ça, le relief ;
//   ③ UN ÉCART DE TEINTE ASSUMÉ entre les deux sources (pêche → miel, ciel → cyan…). Il reste petit,
//      mais il n'est plus nul.
//
// ⛔ ET « TRÈS DOUX » EST TENU, pas sacrifié : le point le plus dense culmine à 46 % **dans un angle**,
// sur une surface qui décroît jusqu'à zéro. La quantité d'encre moyenne sur l'écran est plus FAIBLE
// qu'avant — simplement répartie de façon à se voir. Un fond clair reste clair, un fond sombre reste
// sombre, et le pic est en haut à gauche, sous la barre de titre, jamais sous un montant.
// ⚠️ Ça se REGARDE, ça ne se relit pas : `npm run voir -- degrade:aube`, `boutique-degrades`.
// 📌 `tests/degrades.test.js` MESURE l'amplitude d'opacité de chaque entrée et refuse une nappe plate.
const GF_DEGRADES = [
  { cle:"aube",     nom:"Aube",     dit:"Une lueur rose qui éclot en haut, et du miel qui monte du bas.",
    css:"radial-gradient(115% 80% at 12% 4%, rgba(239,180,157,.4) 0%, rgba(239,180,157,.16) 42%, rgba(239,180,157,0) 72%), linear-gradient(196deg, rgba(248,223,184,0) 44%, rgba(248,223,184,.26) 100%)" },
  { cle:"brise",    nom:"Brise",    dit:"Un ciel qui s'ouvre en haut, un cyan qui affleure en bas.",
    css:"radial-gradient(115% 80% at 12% 4%, rgba(149,188,224,.4) 0%, rgba(149,188,224,.16) 42%, rgba(149,188,224,0) 72%), linear-gradient(196deg, rgba(173,223,227,0) 44%, rgba(173,223,227,.26) 100%)" },
  { cle:"menthe",   nom:"Menthe",   dit:"Vert d'eau en haut, vert tendre qui remonte.",
    css:"radial-gradient(115% 80% at 12% 4%, rgba(153,207,174,.4) 0%, rgba(153,207,174,.16) 42%, rgba(153,207,174,0) 72%), linear-gradient(196deg, rgba(188,224,172,0) 44%, rgba(188,224,172,.26) 100%)" },
  { cle:"glycine",  nom:"Glycine",  dit:"Un lilas qui s'allume, un mauve qui répond.",
    css:"radial-gradient(115% 80% at 12% 4%, rgba(172,160,216,.4) 0%, rgba(172,160,216,.16) 42%, rgba(172,160,216,0) 72%), linear-gradient(196deg, rgba(212,170,215,0) 44%, rgba(212,170,215,.26) 100%)" },
  { cle:"sable",    nom:"Sable",    dit:"Sable chaud dans l'angle, lin sur le reste.",
    css:"radial-gradient(115% 80% at 12% 4%, rgba(225,201,153,.4) 0%, rgba(225,201,153,.16) 42%, rgba(225,201,153,0) 72%), linear-gradient(196deg, rgba(240,228,200,0) 44%, rgba(240,228,200,.26) 100%)" },
  { cle:"ardoise",  nom:"Ardoise",  dit:"Un gris bleuté qui descend, très calme.",
    css:"radial-gradient(115% 80% at 12% 4%, rgba(155,171,189,.4) 0%, rgba(155,171,189,.16) 42%, rgba(155,171,189,0) 72%), linear-gradient(196deg, rgba(200,206,212,0) 44%, rgba(200,206,212,.26) 100%)" },
  { cle:"corail",   nom:"Corail",   dit:"Corail dans l'angle, abricot qui monte.",
    css:"radial-gradient(115% 80% at 12% 4%, rgba(234,155,142,.4) 0%, rgba(234,155,142,.16) 42%, rgba(234,155,142,0) 72%), linear-gradient(196deg, rgba(242,198,161,0) 44%, rgba(242,198,161,.26) 100%)" },
  // 🔴 SA DEUXIÈME COULEUR ÉTAIT UN VERT (corrigé le 2026-09-17, sa remontée : « pour la couleur lagon,
  // je sais pas pourquoi mais il y a une autre couleur, un genre de vert »). Elle valait
  // `rgba(164,219,186)` — à trois points près la couleur basse de « Menthe », `rgba(188,224,172)`.
  // Un lagon qui verdit n'est plus un lagon, et deux fonds vivants sur douze se ressemblaient.
  // Elle est maintenant une vraie aigue-marine, distincte de Menthe (vert) ET de Brise (cyan).
  { cle:"lagon",    nom:"Lagon",    dit:"Turquoise qui éclot, aigue-marine en dessous.",
    css:"radial-gradient(115% 80% at 12% 4%, rgba(130,201,199,.4) 0%, rgba(130,201,199,.16) 42%, rgba(130,201,199,0) 72%), linear-gradient(196deg, rgba(140,208,206,0) 44%, rgba(140,208,206,.26) 100%)" },
  { cle:"prune",    nom:"Prune",    dit:"Prune en haut, framboise qui affleure.",
    css:"radial-gradient(115% 80% at 12% 4%, rgba(167,135,179,.4) 0%, rgba(167,135,179,.16) 42%, rgba(167,135,179,0) 72%), linear-gradient(196deg, rgba(208,151,178,0) 44%, rgba(208,151,178,.26) 100%)" },
  { cle:"ocean",    nom:"Océan",    dit:"Bleu profond dans l'angle, indigo sur le bas.",
    css:"radial-gradient(115% 80% at 12% 4%, rgba(114,154,201,.4) 0%, rgba(114,154,201,.16) 42%, rgba(114,154,201,0) 72%), linear-gradient(196deg, rgba(138,147,205,0) 44%, rgba(138,147,205,.26) 100%)" },
  { cle:"cendre",   nom:"Cendre",   dit:"Taupe qui s'éteint vers un gris chaud.",
    css:"radial-gradient(115% 80% at 12% 4%, rgba(187,175,163,.4) 0%, rgba(187,175,163,.16) 42%, rgba(187,175,163,0) 72%), linear-gradient(196deg, rgba(215,206,196,0) 44%, rgba(215,206,196,.26) 100%)" },
  { cle:"orpale",   nom:"Or pâle",  dit:"Un or clair qui éclot, du miel qui remonte.",
    css:"radial-gradient(115% 80% at 12% 4%, rgba(222,198,132,.4) 0%, rgba(222,198,132,.16) 42%, rgba(222,198,132,0) 72%), linear-gradient(196deg, rgba(237,222,174,0) 44%, rgba(237,222,174,.26) 100%)" },
];
const DEGRADES = GF_DEGRADES.map(d => d.cle);
try { window.GF_DEGRADES = GF_DEGRADES; window.DEGRADES = DEGRADES; } catch (e) {}
/** Le dégradé posé, ou "" (aucun). ⚠️ Une clé inconnue vaut « aucun » : un dégradé retiré du
 *  catalogue ne doit pas laisser un écran avec une classe à laquelle plus rien ne répond. */
function degradeActuel() {
  // ⭐ Un essai de fond vivant (2.31.167) passe devant l'enregistré, comme pour le fond et le thème.
  if (GF_ESSAI && GF_ESSAI.degrade !== undefined) return DEGRADES.indexOf(GF_ESSAI.degrade) >= 0 ? GF_ESSAI.degrade : "";
  if (GF_HORS_COMPTE) return "";   // hors compte : ni fond vivant ni dégradé d'un compte (2.32.47)
  try {
    const v = localStorage.getItem("gf_degrade") || "";
    return DEGRADES.indexOf(v) >= 0 ? v : "";
  } catch (e) { return ""; }
}
/** Le fond vivant VRAIMENT enregistré, essai ignoré. */
function degradeEnregistre() { const g = GF_ESSAI; GF_ESSAI = null; const v = degradeActuel(); GF_ESSAI = g; return v; }
function choisirDegrade(cle) {
  try { localStorage.setItem("gf_degrade", DEGRADES.indexOf(cle) >= 0 ? cle : ""); localStorage.setItem("gf_apparence_ts", String(Date.now())); } catch (e) {}
  appliquerApparence();
}
/** ⭐ L'INTENSITÉ DU FOND VIVANT (2.31.168, sa demande : « réduis encore la lumière, et pourquoi pas une option
 *  pour régler l'opacité nous-mêmes »). Posé en variable CSS `--vivant-intensite` sur `<html>`, lue par
 *  `.gf-bulle` (l'app) et `.ap-vivant-lum` (l'aperçu). Suit le compte via `apparenceCompte.intensite`.
 *
 *  ⭐⭐ L'ÉCHELLE A ÉTÉ REBASÉE LE 2026-09-16 (2.31.169), sa demande : « mets le 20 % par défaut pour
 *  l'intensité des fonds vivants […] le plus petit c'est 10 %, le plus haut c'est 90 % ».
 *  AVANT : 20–100, défaut 50 — c'est-à-dire le milieu de l'échelle. APRÈS : 10–90, défaut 20.
 *  📌 CE QUE ÇA VEUT DIRE, ET C'EST SON MOT : « baissé à 20 % on ne le voit presque plus, c'est limite
 *  invisible ». C'est exactement l'effet voulu par défaut — un fond vivant est un fond, pas un écran de
 *  veille. Qui veut le voir monte le curseur ; personne ne subit la lumière sans l'avoir demandée.
 *  ⚠️ UNE VALEUR DÉJÀ ENREGISTRÉE HORS BORNES NE SE PERD PAS EN SILENCE : 100 (l'ancien maximum) tombe
 *  au nouveau maximum, 90, via `choisirIntensiteVivant()` au prochain réglage ; la LECTURE, elle, borne
 *  aussi, sinon un ancien 100 continuerait d'éclairer plus fort que ce que le curseur peut atteindre —
 *  un réglage qu'aucun geste ne pourrait plus reproduire. */
const VIVANT_INTENSITE_DEFAUT = 50, VIVANT_INTENSITE_MIN = 10, VIVANT_INTENSITE_MAX = 90;
/**
 * ⭐⭐⭐ LE CURSEUR N'EST PLUS L'OPACITÉ — IL EST DEUX FOIS ET DEMIE PLUS DOUX (2.32.0, sa correction du
 * 2026-09-16 : « l'effet du fond vivant en 20 % là doit devenir le nouveau 50 % par défaut, et le nouveau
 * 20 % doit être encore moins visible que le 20 % actuel »).
 *
 * 🔴 CE QUE J'AVAIS COMPRIS DE TRAVERS LA VEILLE : il avait demandé « mets le 20 % par défaut », et j'avais
 * déplacé le DÉFAUT à 20 en laissant l'échelle telle quelle. Ce qu'il voulait, c'est déplacer L'ÉCHELLE :
 * garder le curseur au milieu (50, là où on s'attend à trouver un réglage par défaut) et faire en sorte
 * que ce milieu rende ce que 20 % rendait avant. Le défaut revient donc à 50.
 *
 * 📌 LA RÈGLE, EN UNE LIGNE : `opacité = valeur / 250` (au lieu de `/ 100`).
 *      curseur 10 → 0,04   ·   20 → 0,08   ·   50 → 0,20   ·   90 → 0,36
 *    Le milieu (50) rend exactement l'ancien 20 %, et le minimum (10) est cinq fois plus discret que
 *    l'ancien 20 % — « encore moins visible », c'était sa seconde phrase.
 * ⛔ LE CALCUL EST ICI, DANS UNE FONCTION, ET PAS DANS `appliquerApparence()` : `tests/degrades.test.js`
 * l'EXÉCUTE pour vérifier les trois points de repère. Une division écrite au milieu d'une autre fonction
 * ne se rejoue pas, et c'est exactement le genre de règle qu'on croit connaître et qui dérive.
 */
// 🔴🔴 L'ÉCHELLE A ÉTÉ REFAITE LE 2026-09-16 AU SOIR, ET C'ÉTAIT UNE VRAIE PANNE (sa remontée : « j'ai
// acheté un fond vivant Prune, je l'ai placé, mais il n'y a rien qui apparaît »). MESURÉ AU BANC
// `fond-vivant-se-voit` : les trois lumières sortaient à **0,076 d'opacité réelle** (0,38 d'alpha × 0,20
// d'échelle). Un prune pâle à 7,6 % sur un fond clair, c'est du blanc.
//
// ⛔ DEUX DE MES CHANGEMENTS SE SONT ADDITIONNÉS, ET AUCUN N'ÉTAIT FAUX TOUT SEUL :
//   ① 2.31.170 — j'ai retiré l'APLAT (sa demande : « retire les dégradés définitivement »). Or c'était
//      LUI qui portait la couleur : il se peignait à pleine force, sans jamais passer par l'intensité.
//      Les trois lumières, elles, avaient été réglées pour passer PAR-DESSUS lui — d'où leurs alphas faibles.
//   ② 2.32.0 — j'ai divisé l'échelle par 2,5 pour adoucir (sa demande : « le 50 % doit rendre l'ancien 20 % »).
//   Après ①, les lumières étaient la SEULE source de couleur ; après ②, elles ne portaient plus rien.
// 📌 ET L'APERÇU NE POUVAIT PAS LE MONTRER : il peint à opacité FIXE (0,85), justement pour rester lisible.
//    La fenêtre était donc juste, et l'écran vide. Une vérification qui ne passe pas par le vrai écran ne
//    voit rien de tout ça — d'où le banc, qui mesure des pixels.
//
// ✅ L'ÉCHELLE EST DÉSORMAIS `valeur / 90` : le HAUT du curseur (90) donne les lumières à pleine force, et
// tout le reste s'y range. 10 → 0,11 · 20 → 0,22 · 50 → 0,56 (le défaut) · 90 → 1,00.
// Effet réel au défaut : 0,38 × 0,56 = **0,21**, soit l'ordre de grandeur de l'ancien aplat (0,26 à 0,46) —
// sauf qu'il est maintenant concentré dans trois lumières qui dérivent, au lieu d'être étalé partout.
const VIVANT_ECHELLE = 90;
function opaciteVivant(valeur) {
  const v = Number(valeur);
  const n = (v >= 1) ? Math.min(VIVANT_INTENSITE_MAX, Math.max(VIVANT_INTENSITE_MIN, v)) : VIVANT_INTENSITE_DEFAUT;
  return n / VIVANT_ECHELLE;
}
function intensiteVivant() {
  try {
    const v = parseInt(localStorage.getItem("gf_vivant_intensite"), 10);
    if (!(v >= 1)) return VIVANT_INTENSITE_DEFAUT;
    return Math.min(VIVANT_INTENSITE_MAX, Math.max(VIVANT_INTENSITE_MIN, v));
  } catch (e) { return VIVANT_INTENSITE_DEFAUT; }
}
function choisirIntensiteVivant(v) {
  const n = Math.min(VIVANT_INTENSITE_MAX, Math.max(VIVANT_INTENSITE_MIN, Math.round(Number(v) || VIVANT_INTENSITE_DEFAUT)));
  try { localStorage.setItem("gf_vivant_intensite", String(n)); localStorage.setItem("gf_apparence_ts", String(Date.now())); } catch (e) {}
  appliquerApparence();
}
/** Le CSS d'un dégradé, ou "" — la seule lecture autorisée du catalogue. */
function cssDegrade(cle) {
  const d = GF_DEGRADES.find(x => x.cle === cle);
  return d ? d.css : "";
}

function migrerApparence() {
  try {
    // ⛔ LE FOND « NOIR » N'EXISTE PLUS : on rapatrie ceux qui l'avaient sur « Sombre ». Ce contrôle
    // passe AVANT le garde « déjà migré », sinon les comptes déjà migrés — c'est-à-dire tous ceux qui
    // avaient choisi Noir — ne seraient jamais rattrapés et se retrouveraient sur un fond dont aucune
    // classe CSS ne répond. Un réglage qui pointe vers rien retombe en silence sur le défaut.
    if (localStorage.getItem("gf_fond") === "noir") localStorage.setItem("gf_fond", "sombre");
    // ⛔ MÊME CHOSE POUR BEIGE ET GRAPHITE, retirés le 2026-09-06 (voir la note de `FONDS`). Chacun
    // rejoint son voisin le plus proche — `papier` est l'autre crème, `claude` l'autre gris presque
    // noir — et surtout PAS le défaut : quelqu'un qui avait choisi un fond sombre se serait réveillé
    // en blanc, ce qui est le contraire d'une migration réussie.
    // ⚠️ Ces deux lignes passent, elles aussi, AVANT le garde « déjà migré » : les comptes concernés
    // sont précisément ceux qui ont déjà un `gf_fond` écrit.
    const RETIRES={beige:"papier", graphite:"claude"};
    const fondEcrit=localStorage.getItem("gf_fond");
    if (fondEcrit && RETIRES[fondEcrit]) localStorage.setItem("gf_fond", RETIRES[fondEcrit]);
    if (localStorage.getItem("gf_fond")) return;          // déjà migré
    // ⭐ LE DÉFAUT EST « CLAIR » DEPUIS LE 2026-09-05 (sa décision : « mets le thème blanc par défaut
    // sur l'application »). ⚠️ Ça ne concerne QUE les installations neuves : tout compte existant a
    // déjà son `gf_fond` écrit, et sort à la ligne au-dessus sans être touché.
    const ancien = localStorage.getItem("gf_theme") || "clair";
    localStorage.setItem("gf_fond", FONDS.includes(ancien) ? ancien : "clair");
    localStorage.setItem("gf_theme", THEMES.includes(ancien) ? ancien : "solvea");
  } catch (e) {}
}
migrerApparence();

// ⚠️ Le repli est « clair » depuis le 2026-09-05, et il doit rester d'accord avec `migrerApparence` :
// deux défauts différents donneraient un premier écran d'une couleur et le suivant d'une autre.
// ==================================================================================================
// ⭐⭐⭐ L'ESSAI SUR SON VRAI ÉCRAN — G1 (2026-09-08, sa liste de propositions)
// ==================================================================================================
//
// 🔴 CE QUI EXISTAIT : un thème ou un fond se regarde sur une MAQUETTE — trois petits écrans dessinés
// dans une fenêtre. C'est utile pour comparer vite, et c'est insuffisant pour décider : on ne voit ni
// ses propres montants, ni ses catégories, ni la longueur réelle de ses libellés, ni le rendu de la
// barre du bas. Sa proposition : « l'aperçu se pose sur VOTRE vrai Accueil, avec vos vrais chiffres,
// et un bouton pour revenir ».
//
// ⛔⛔ RIEN N'EST ENREGISTRÉ, ET C'EST TOUTE LA MÉCANIQUE. `GF_ESSAI` vit en mémoire, jamais dans
// `localStorage` : fermer l'application, la recharger, ou même la faire planter ramène donc
// l'apparence d'avant, sans qu'aucun code de nettoyage ait à s'exécuter. Un essai qu'on pourrait
// « oublier posé » serait un changement de thème déguisé.
//
// ⛔ ET IL PASSE PAR `themeActuel()` / `fondActuel()`, c'est-à-dire par LE point de lecture unique de
// l'apparence. Peindre l'écran à côté aurait produit un deuxième chemin d'application — et ce dépôt
// sait ce que coûtent deux chemins pour la même chose (les trois rendus du widget, les trente
// `innerHTML` de notifications). Ici, `appliquerApparence()` continue de tout faire : les classes sur
// `<html>`, le dégradé, le fond perso, et la couleur de la barre système.
//
// ⚠️ UN CHOIX RÉEL MET FIN À L'ESSAI, toujours. `choisirTheme()` et `choisirFond()` l'effacent avant
// d'écrire : sans ça, on choisirait un thème pour de bon et l'écran continuerait d'en montrer un
// autre — l'essai gagnerait contre la décision.
let GF_ESSAI = null;   // {theme} ou {fond} — jamais les deux, un essai ne compare qu'un axe

// ⭐⭐⭐ L'ESSAI DURE 30 SECONDES, ET PAS PLUS (2.31.137, son signalement du 2026-09-13, images 2 et 3).
//
// > Ses mots : « je peux quand même utiliser sans problème, je trouve ça pas fou, car les personnes
// > peuvent tout simplement relancer l'essai sur leur écran pour avoir "le thème" ».
//
// 🔴 IL A RAISON, ET C'EST UN TROU DANS L'ÉCONOMIE, PAS UN DÉTAIL D'ERGONOMIE. Un essai sans fin
// N'EST PAS un essai : c'est le thème, gratuitement, à la seule condition de rappuyer sur un bouton
// de temps en temps. Tout ce qui se vend dans cette boutique s'obtenait ainsi.
//
// ✅ TRENTE SECONDES — son choix, posé en question. C'est assez pour parcourir les quatre onglets
// avec ses vrais chiffres (ce pour quoi l'essai existe, cf. 2.31.88), et trop court pour remplacer
// l'achat. ⛔ Le compte à rebours repart à zéro à chaque nouvel essai, jamais il ne se cumule.
//
// ⚠️ LE MINUTEUR VIT ICI, AVEC L'ESSAI, ET NULLE PART AILLEURS. Le poser dans l'écran de la boutique
// l'aurait laissé tourner sur un essai déjà arrêté — ou pire, l'aurait oublié quand l'essai est
// arrêté d'ailleurs (le bouton « Revenir », un choix réel, un changement de compte). Une seule
// fonction pose l'essai : c'est elle qui arme et désarme.
const GF_ESSAI_DUREE = 30000;
let _gfEssaiMinuteur = null;
let _gfEssaiFin = 0;
// ⭐⭐⭐ L'INTENSITÉ D'AVANT L'ESSAI (2.32.44, sa décision du 2026-09-23 : « Revenir » remet tout
// comme avant, l'intensité comprise).
// ⛔ POURQUOI C'EST INDISPENSABLE : le curseur de la barre d'essai écrit dans `localStorage` à
// chaque relâchement, comme celui de Personnalisation — c'est ce qui permet de voir l'effet en
// direct. Sans mémoire, quelqu'un qui essaie un fond vivant, pousse l'intensité pour voir, puis
// touche « Revenir », garderait ce réglage sans l'avoir voulu. Il aurait changé son apparence en
// ayant seulement voulu REGARDER, ce qui est exactement le contraire de ce que le bouton promet.
// ⚠️ `null` = rien à rendre. On ne mémorise QUE pour un essai de fond vivant : un essai de thème ne
// touche pas à l'intensité, et une valeur mémorisée pour rien finirait par être rendue à tort.
let _gfIntensiteAvantEssai = null;

/** Pose (ou retire) l'essai. `null` remet l'apparence enregistrée. */
function gfEssayerApparence(o) {
  const neuf = (o && (o.theme || o.fond || o.degrade !== undefined)) ? o : null;
  // On retient l'intensité au DÉBUT d'un essai de fond vivant, jamais pendant : un second appel
  // (changer de fond vivant sans quitter l'essai) écraserait la vraie valeur d'origine par celle
  // qu'on vient de régler, et « Revenir » ne rendrait plus rien.
  if (neuf && neuf.degrade !== undefined && !GF_ESSAI) {
    try { _gfIntensiteAvantEssai = intensiteVivant(); } catch (e) { _gfIntensiteAvantEssai = null; }
  }
  GF_ESSAI = neuf;
  if (_gfEssaiMinuteur) { clearTimeout(_gfEssaiMinuteur); _gfEssaiMinuteur = null; }
  _gfEssaiFin = 0;
  if (GF_ESSAI) {
    _gfEssaiFin = Date.now() + GF_ESSAI_DUREE;
    _gfEssaiMinuteur = setTimeout(function () {
      _gfEssaiMinuteur = null; _gfEssaiFin = 0;
      if (!GF_ESSAI) return;
      GF_ESSAI = null;
      // ⛔ L'EXPIRATION EST UN RETOUR, PAS UNE VALIDATION : elle rend l'intensité comme le ferait
      // « Revenir ». Sans cette ligne, laisser filer les trente secondes garderait le réglage —
      // c'est-à-dire que ne rien faire aurait un effet, et pas le bouton prévu pour ça.
      try { gfRendreIntensiteAvantEssai(); } catch (e) {}
      appliquerApparence();
      // ⛔ ON LE DIT. Un écran qui reprend son apparence tout seul, sans un mot, se lit comme un
      // bug — c'est exactement le reproche qu'il a fait au rechargement des pages (règle n° 30).
      try { if (typeof gfToast === "function") gfToast("Fin de l'essai — votre apparence est revenue."); } catch (e) {}
      try { if (typeof window.gfRendreBarreEssai === "function") window.gfRendreBarreEssai(); } catch (e) {}
      try { if (typeof window.gfRafraichirApresTheme === "function") window.gfRafraichirApresTheme(); } catch (e) {}
    }, GF_ESSAI_DUREE);
  }
  appliquerApparence();
}
/** Les SECONDES qu'il reste à l'essai, `0` quand il n'y en a pas. Le compte à rebours s'affiche dans
 *  la barre : trente secondes qui s'écoulent sans qu'on les voie seraient une disparition, pas une
 *  limite — et c'est très exactement le reproche qu'il a fait au rechargement des pages. */
function gfEssaiRestant() {
  if (!GF_ESSAI || !_gfEssaiFin) return 0;
  return Math.max(0, Math.ceil((_gfEssaiFin - Date.now()) / 1000));
}
/** Rend l'intensité d'avant l'essai, s'il y en avait une à rendre. Sans effet sinon. */
function gfRendreIntensiteAvantEssai() {
  if (_gfIntensiteAvantEssai == null) return;
  const v = _gfIntensiteAvantEssai;
  _gfIntensiteAvantEssai = null;
  try { choisirIntensiteVivant(v); } catch (e) {}
}
/** On GARDE : le réglage du moment devient le bon, il n'y a plus rien à rendre. */
function gfOublierIntensiteAvantEssai() { _gfIntensiteAvantEssai = null; }
/** L'essai en cours, ou `null`. Sert à l'écran qui affiche la barre « Aperçu ». */
function gfEssaiEnCours() { return GF_ESSAI; }

// ⭐⭐ LE CLAIR / SOMBRE DES PAGES HORS COMPTE (2.32.47, sa demande du 2026-09-24).
// Connexion, création de compte, confirmation : aucune de ces pages n'appartient à un compte. Elles
// lisaient pourtant `gf_fond`, le réglage du DERNIER compte ouvert sur le téléphone — et le bouton du
// site l'ÉCRIVAIT, ce qui repeignait ce compte à sa reconnexion suivante (le téléphone, plus récent,
// gagnait l'arbitrage d'`adopterApparenceCompte`).
// ✅ Elles ont désormais LEUR réglage, `gf_fond_accueil`, qui ne touche jamais à `gf_fond`. Une page
// se déclare hors compte par `<html data-hors-compte>`. Sa règle, mot pour mot : un compte qui a déjà
// son apparence la GARDE ; seul un compte NEUF part de la couleur choisie ici (morceau 18,
// `adopterApparenceCompte`).
// 📌 Pas de `cleCompte()` pour cette clé, et c'est voulu : elle existe AVANT tout compte, par définition.
function fondAccueil() { try { return localStorage.getItem("gf_fond_accueil") === "sombre" ? "sombre" : "clair"; } catch (e) { return "clair"; } }
// Le bouton lui-même vit dans `clair-sombre.js`, chargé par les SEULES pages hors compte : config.js est
// lu à chaque ouverture de l'application, et son plafond de poids (tests/poids-demarrage) ne se relève
// pas pour un bouton qu'aucune page de l'application n'affiche.
function fondActuel()  { if (GF_ESSAI && GF_ESSAI.fond) return GF_ESSAI.fond; if (GF_HORS_COMPTE) return fondAccueil(); try { const f=localStorage.getItem("gf_fond"); return (f && f!=="noir") ? f : (f==="noir" ? "sombre" : "clair"); } catch (e) { return "clair"; } }
function themeActuel() { if (GF_ESSAI && GF_ESSAI.theme) return GF_ESSAI.theme; if (GF_HORS_COMPTE) return "solvea"; try { return localStorage.getItem("gf_theme") || "solvea"; } catch (e) { return "solvea"; } }
/** L'apparence VRAIMENT enregistrée, essai ignoré. C'est elle qu'on rétablit en quittant l'essai. */
function fondEnregistre()  { const g=GF_ESSAI; GF_ESSAI=null; const v=fondActuel();  GF_ESSAI=g; return v; }
function themeEnregistre() { const g=GF_ESSAI; GF_ESSAI=null; const v=themeActuel(); GF_ESSAI=g; return v; }

// ==================================================================================================
// ⭐ TROISIÈME AXE : LA TAILLE DE L'AFFICHAGE (2026-08-15, sa demande — « trois crans »)
// ==================================================================================================
// Même mécanique que le fond et le thème : une classe sur <html>, une clé dans localStorage, et le
// CSS fait le reste (tête de styles.css). Trois crans seulement, volontairement : un curseur continu
// aurait donné dix rendus à peu près identiques et un réglage impossible à décrire.
//
// 🔴 POURQUOI ÇA AGRANDIT TOUT L'ÉCRAN, ET PAS SEULEMENT LES POLICES. Il l'a tranché le 2026-08-15.
// L'application écrit 609 tailles de police EN DUR, en pixels, réparties dans app.css, styles.css,
// app.html, profil.html, index.html et app.js. Ne grossir que ces 609 valeurs laisserait toutes les
// hauteurs fixes en place (champs de 42 px, pastilles, barres de progression, ronds d'icône) : le
// texte déborderait de ses boîtes, écran par écran, sans qu'aucun test ne le voie. `zoom` agrandit la
// boîte EN MÊME TEMPS que son contenu — l'app reste proportionnée au pixel près, comme le réglage
// « Taille d'affichage » d'Android, dont c'est exactement le comportement.
//
// ⚠️ `zoom` et pas `transform: scale()` : `scale` ne rend pas sa place dans le flux (l'app garderait
// la taille d'avant et déborderait ou laisserait un vide), et il casse `position: fixed`, dont
// dépendent la barre d'onglets, les fenêtres et le verrou.
const TAILLES = ["petit", "normal", "grand"];
function tailleActuelle() {
  try { const t = localStorage.getItem("gf_taille"); return TAILLES.includes(t) ? t : "normal"; }
  catch (e) { return "normal"; }
}
function choisirTaille(nom) {
  try { localStorage.setItem("gf_taille", nom); } catch (e) {}
  appliquerApparence();
  // H3 : le cran de Solvea change, la part laissée à la taille du téléphone doit suivre (voir plus bas).
  if (typeof gfAppliquerTailleSysteme === "function") gfAppliquerTailleSysteme();
}
// ⭐⭐⭐ H3 — LE TÉLÉPHONE EST LA BASE, LE RÉGLAGE DE SOLVEA SE POSE PAR-DESSUS (2026-09-11, sa
// décision validée le 2026-09-08 : « Normal = le téléphone, Grand = un cran de plus, avec un plafond
// pour que ça ne double pas »).
//
// 🔴 CE QUI SE PASSAIT : la WebView de l'APK ignorait la taille de texte réglée dans Android. Quelqu'un
// qui avait tout agrandi sur son téléphone — souvent parce qu'il lit mal — retrouvait dans Solvea la
// taille d'origine, et devait la chercher une seconde fois dans nos réglages.
//
// 📌 DEUX ÉTAGES, ET ILS NE FONT PAS LA MÊME CHOSE :
//   · le TÉLÉPHONE règle le TEXTE de la WebView (`TextZoom`, plugin officiel Capacitor) ;
//   · le cran de SOLVEA (Petit / Normal / Grand) reste le `zoom` CSS ci-dessus, qui agrandit TOUT —
//     texte, icônes, marges — comme « Taille d'affichage » d'Android.
// ⛔ LE PLAFOND PORTE SUR LE PRODUIT DES DEUX, et c'est tout son intérêt : un téléphone réglé à 200 %
// (Android 14 va jusque-là) multiplié par « Grand » ferait passer un montant sur deux lignes et
// déborder chaque grille. Le texte ne dépasse jamais 1,4 fois la taille d'origine, tous étages
// confondus ; en dessous du plafond, c'est le téléphone qui décide, vers le haut comme vers le bas
// (avec un plancher à 0,85, en deçà duquel un montant se lit mal).
// ⚠️ FONCTION PURE : `tests/taille-systeme.test.js` l'exécute. Le plugin, lui, ne se vérifie que sur un
// vrai téléphone.
const ZOOM_TAILLE = { petit: 0.9, normal: 1, grand: 1.12 };   // ⚠️ les mêmes valeurs que styles.css (`:root.taille-*`)
const PLAFOND_TEXTE = 1.4, PLANCHER_TEXTE = 0.85;
function zoomTexteSysteme(preference, taille) {
  const pref = Number(preference) > 0 ? Number(preference) : 1;
  const facteurApp = ZOOM_TAILLE[taille] || 1;
  const zoom = Math.min(Math.max(pref, PLANCHER_TEXTE), PLAFOND_TEXTE / facteurApp);
  return Math.round(zoom * 100) / 100;
}

// ==================================================================================================
// ⭐⭐⭐ QUATRIEME AXE : LA FLUIDITE (2026-08-31, sa demande -- et elle n'est pas arrivee sous ce nom)
// ==================================================================================================
// **Sa phrase** : « j'ignore si l'application est grosse sur un telephone qui est assez "merdique",
// donc proposer une option dans les parametres ou il y a le choix entre 24 FPS - 30 - 60 - 120,
// qu'en penses-tu ? »
//
// 🔴 CE QU'UN SELECTEUR DE FPS N'AURAIT PAS FAIT, ET POURQUOI CE REGLAGE EXISTE A LA PLACE. Une
// application web ne CHOISIT pas la cadence de son ecran : c'est Android qui l'impose, et
// `requestAnimationFrame` recoit la cadence qu'on lui donne. Un menu « 24 / 30 / 60 / 120 » aurait
// donc affiche un choix sans effet -- le pire genre de reglage, celui qui fait croire qu'on a agi.
// Ce qui coute vraiment cher sur un telephone lent, c'est le NOMBRE d'animations : la cascade des
// cartes (une animation par ligne, sur des listes de trois cents lignes), les chiffres qui montent
// (un `requestAnimationFrame` par compteur, 560 ms chacun) et le vol des Nacres.
//
// ⭐ TROIS CRANS, ET C'EST SON choix parmi les options qui lui ont ete presentees :
//   · `complete` -- tout, comme avant. Le defaut.
//   · `reduite`  -- plus de cascade, plus de chiffres qui montent, plus de vibration. Les
//                    transitions courtes du CSS restent : l'application ne devient pas raide.
//   · `aucune`   -- meme chose, PLUS toutes les transitions et animations CSS coupees d'un coup.
//
// ⚠️ LE REGLAGE SYSTEME PASSE TOUJOURS DEVANT. « Reduire les animations » d'Android existe pour les
// gens que le mouvement rend malades : il ne se contourne pas, et `complete` ne le rattrape pas.
// ⚠️ Dans le TELEPHONE, pas dans le compte -- exactement comme la taille d'affichage juste au-dessus.
// Un telephone lent et un ordinateur ne veulent pas le meme cran.
const FLUIDITES = ["complete", "reduite", "aucune"];
function fluiditeActuelle() {
  try { const f = localStorage.getItem("gf_fluidite"); return FLUIDITES.includes(f) ? f : "complete"; }
  catch (e) { return "complete"; }
}
function choisirFluidite(nom) { try { localStorage.setItem("gf_fluidite", nom); } catch (e) {} appliquerApparence(); }
// ⚠️ LA SEULE REPONSE A « faut-il animer ? », POUR TOUT LE PROJET. `app.js` l'appelle par
// `_mouvementReduit()`, qui n'est qu'un renvoi vers cette fonction : deux definitions du meme test
// auraient divergé au premier cran ajouté.
function mouvementReduitGlobal() {
  try { if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return true; } catch (e) {}
  return fluiditeActuelle() !== "complete";
}

// --------------------------------------------------------------------------------------------------
// 🔴 LA SEULE VRAIE DIFFICULTÉ DE `zoom` : IL EXISTE DEUX PIXELS, ET IL NE FAUT JAMAIS LES ADDITIONNER
// --------------------------------------------------------------------------------------------------
// Dès que le zoom n'est plus 1, deux unités cohabitent dans la page :
//   · le pixel LOCAL   — celui de `offsetWidth`, `offsetTop`, et de tout ce qu'on écrit dans
//                        `style.left` / `style.top`. C'est le pixel « avant zoom » ;
//   · le pixel ÉCRAN   — celui de `window.innerWidth` / `innerHeight`, qui ne bougent JAMAIS avec le
//                        zoom (vérifié : 375 alors que zoom vaut 1,12).
// Une position calculée en mélangeant les deux est fausse d'un facteur 1,12 — c'est ce qui aurait
// décentré les bulles d'info dès le cran « Grand ». `enLocal()` fait la conversion, une fois, au bon
// endroit.
//
// ⚠️ ET `getBoundingClientRect()` A CHANGÉ DE CAMP EN COURS DE ROUTE. Chrome ≥ 128 (le zoom
// standardisé) le renvoie en pixels ÉCRAN ; les moteurs antérieurs le renvoyaient en pixels LOCAUX.
// L'APK tourne sur la WebView du téléphone, dont on ne choisit pas la version : plutôt que de tester
// un numéro de version, on MESURE le rapport sur `<html>` lui-même, dont on connaît les deux
// mesures. `ratioRect()` répond 1 sur les vieux moteurs et le zoom sur les nouveaux, sans devinette.
function facteurZoom() {
  try { const z = parseFloat(getComputedStyle(document.documentElement).zoom); return (z > 0) ? z : 1; }
  catch (e) { return 1; }
}
function ratioRect() {
  try {
    const h = document.documentElement;
    if (!h.offsetWidth) return 1;
    const r = h.getBoundingClientRect().width / h.offsetWidth;
    return (Math.abs(r - 1) < 0.01) ? 1 : facteurZoom();
  } catch (e) { return 1; }
}
// Un rectangle d'élément, TOUJOURS en pixels locaux — donc comparable à `offsetWidth` et directement
// utilisable dans `style.left`. À utiliser partout où une mesure sert à POSITIONNER quelque chose.
function rectLocal(el) {
  const b = el.getBoundingClientRect(), k = ratioRect();
  if (k === 1) return b;
  return { left:b.left/k, top:b.top/k, right:b.right/k, bottom:b.bottom/k, width:b.width/k, height:b.height/k };
}
// La position d'un événement pointeur, en pixels locaux. `clientX`/`clientY` vivent dans le MÊME
// espace que `getBoundingClientRect()` (vérifié à l'exécution avec `elementFromPoint`), donc le même
// diviseur les ramène tous les deux.
// 🔴 C'est LE point sensible des glisser-déposer : un écart de doigt mesuré en pixels écran puis écrit
// dans un `transform` (pixels locaux) fait avancer le bloc de 89 % du geste au cran « Grand » — le
// bloc décroche du doigt, et le repère de réinsertion se décale au fil du glissement.
function ptLocal(e) { const k = ratioRect(); return (k === 1) ? { x:e.clientX, y:e.clientY } : { x:e.clientX/k, y:e.clientY/k }; }
// La fenêtre visible, en pixels locaux.
function vueLocale() { const z = facteurZoom(); return { l: window.innerWidth / z, h: window.innerHeight / z }; }

// ⚠️ On RETIRE toutes les classes connues avant d'en poser une. Sans ça, deux fonds s'empilent sur
// <html> et le dernier déclaré gagne pour toujours — le piège déjà payé le 2026-08-13.
function appliquerApparence() {
  const r = document.documentElement;
  FONDS.forEach(f => r.classList.remove("fond-" + f));
  THEMES.forEach(t => r.classList.remove("theme-" + t));
  TAILLES.forEach(t => r.classList.remove("taille-" + t));
  r.classList.add("taille-" + tailleActuelle());
  // ⚠️ La classe n'est posee que pour le cran « aucune » : c'est le seul qui ait besoin du CSS
  // (couper les transitions). « reduite » se joue entierement en JavaScript, dans `jouerCascade` et
  // `animerNombre` -- il ne raidit donc pas l'interface, il retire seulement ce qui coute cher.
  FLUIDITES.forEach(f => r.classList.remove("fluidite-" + f));
  r.classList.add("fluidite-" + fluiditeActuelle());
  // ⭐⭐⭐ LE « STYLE RICHE » N'EST PLUS UN INTERRUPTEUR, IL EST DANS « COMPLÈTE » (2026-09-06, sa
  // demande : « le bouton style relief etc. doit être intégré dans le choix des différents crans de
  // fluidité pour "Complet" [...] donc retirer le bouton "Style riche" et la description qui va avec »).
  //
  // 📌 POURQUOI C'EST JUSTE, ET PAS SEULEMENT PLUS COURT. Les deux réglages répondaient à la MÊME
  // question — « jusqu'où l'application a-t-elle le droit d'en faire ? » — et ils y répondaient
  // séparément. On pouvait donc être en « Fluidité : aucune » avec des ombres, des dégradés et des
  // couleurs marquées partout : le cran promettait une interface sobre et l'autre réglage la
  // rhabillait. Un seul curseur, trois crans, et le relief est ce que « Complète » ajoute.
  //
  // ⚠️ `profilFinance.styleRiche` N'EST PLUS LU ICI, MAIS LE CHAMP RESTE EN BASE : il est encore écrit
  // par les sauvegardes existantes, et le supprimer casserait la relecture d'un fichier ancien. Il ne
  // décide simplement plus de rien. ⛔ Ne pas le rebrancher sans le lui redemander.
  // ⚠️ Le réglage vit dans le TÉLÉPHONE (`gf_fluidite`) alors que `styleRiche` vivait dans le COMPTE :
  // c'est un changement voulu. Un téléphone lent n'a pas à imposer sa sobriété au second appareil.
  r.classList.toggle("style-riche", fluiditeActuelle() === "complete");
  const fond = fondActuel();
  if (fond !== "personnalise") r.classList.add("fond-" + fond);
  r.classList.add("theme-" + themeActuel());
  if (fond === "personnalise") {
    const c = JSON.parse(localStorage.getItem("gf_theme_perso") || "null");
    if (c) {
      r.style.setProperty("--bg", c.bg);
      r.style.setProperty("--surface", c.surface);
      r.style.setProperty("--text", c.text);
      r.style.setProperty("--border", c.border);
    }
  } else {
    ["--bg","--surface","--text","--border"].forEach(v => r.style.removeProperty(v));
  }
  // ⭐ LE DÉGRADÉ, POSÉ AU MÊME MOMENT QUE LE RESTE (2026-09-07). Il vit dans le style EN LIGNE de
  // `body`, avec l'image perso s'il y en a une — c'est `appliquerFondPerso()` qui compose les deux.
  // ⛔ NE PAS LE PEINDRE DEPUIS UNE FEUILLE DE STYLE : l'image perso s'écrit en ligne, et un style en
  // ligne bat toujours une feuille. Le dégradé y disparaîtrait au premier fond d'écran choisi.
  // ⚠️ La classe `a-degrade` sert au CSS qui a besoin de SAVOIR qu'un voile est posé (rien pour
  // l'instant), pas à le peindre. Elle est là pour qu'un futur ajout n'ait pas à relire ce fichier.
  try {
    const dg = degradeActuel();
    r.classList.toggle("a-degrade", !!dg);
    DEGRADES.forEach(d => r.classList.remove("degrade-" + d));
    if (dg) r.classList.add("degrade-" + dg);
    appliquerFondPerso();   // sans argument : on repeint les deux couches, on ne change pas l'image
  } catch (e) {}
  // ⚠️ EN DERNIER, ET SANS `typeof` : la barre de statut du téléphone se repeint du même geste que
  // l'écran. En dernier parce qu'elle LIT `--bg`, qui vient d'être posé juste au-dessus.
  // ⛔ Aucun garde `typeof gfMajBarreSysteme === "function"` ici : la fonction est déclarée dans CE
  // fichier, donc toujours là. Un tel garde est exactement l'interrupteur silencieux qui a mangé le
  // message de la liaison Google pendant trois versions (HANDOFF, 17e session) — on ne le réintroduit
  // pas pour un appel dont on sait qu'il existe.
  gfMajBarreSysteme();
}
// ⚠️ `GF_ESSAI = null` D'ABORD, ET C'EST INDISPENSABLE (G1, 2026-09-08). Sans cette remise à zéro, on
// choisirait un thème pour de bon et l'écran continuerait d'en montrer un autre : l'essai gagnerait
// contre la décision, et le réglage aurait l'air cassé. Un choix réel met fin à l'essai, toujours.
function choisirFond(nom)  { GF_ESSAI = null; try { localStorage.setItem("gf_fond",  nom); localStorage.setItem("gf_apparence_ts", String(Date.now())); } catch (e) {} appliquerApparence(); }
function choisirTheme(nom) { GF_ESSAI = null; try { localStorage.setItem("gf_theme", nom); localStorage.setItem("gf_apparence_ts", String(Date.now())); } catch (e) {} appliquerApparence(); }
// Ancien nom, gardé parce qu'il est appelé ailleurs : il désigne LE FOND, c'est ce qu'il faisait avant.
function appliquerThemeNomme(nom) { choisirFond(nom); }

// ---- Une couleur est-elle CLAIRE ? Une seule formule, pour tout le projet ----
// ⚠️ UNE SEULE, ET C'EST LE POINT. Cette moyenne pondérée (la luminance perçue : l'œil voit le vert
// bien plus que le bleu) vivait recopiée dans `definirThemePerso`. Elle sert maintenant AUSSI à
// décider la couleur des icônes de la barre de statut — deux copies auraient divergé, et l'une des
// deux se serait tue (REGLE.md D2 : on extrait, on ne recopie pas).
// 📌 Pourquoi la LUMINANCE et non la liste `FONDS_SOMBRES` : cette liste est tenue à la main, et elle
// s'est DÉJÀ trompée — elle a longtemps oublié « Noir », « Claude », « Abysse » et « Forêt » (voir le
// commentaire de `fondEstSombre` juste en dessous). Une couleur lue à l'exécution ne peut pas dériver
// de ce que le CSS affiche vraiment, et elle couvre le thème personnalisé sans rien ajouter.
// Accepte `#rgb`, `#rrggbb` et `rgb(r, g, b)` — les trois formes que rend `getComputedStyle`.
function gfCouleurClaire(couleur) {
  const c = String(couleur || "").trim();
  let r, g, b;
  const rgb = c.match(/^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/i);
  if (rgb) { r = +rgb[1]; g = +rgb[2]; b = +rgb[3]; }
  else {
    let h = c.replace("#", "");
    if (h.length === 3) h = h[0]+h[0]+h[1]+h[1]+h[2]+h[2];
    if (!/^[0-9a-f]{6}$/i.test(h)) return true;   // illisible → on suppose clair, le cas le plus courant
    r = parseInt(h.slice(0,2),16); g = parseInt(h.slice(2,4),16); b = parseInt(h.slice(4,6),16);
  }
  return (r*299 + g*587 + b*114) / 1000 > 150;
}

// ---- La barre de statut du téléphone suit le fond de l'application ----
//
// ⛔⛔ POURQUOI CETTE FONCTION EXISTE, ET CE N'EST PAS UN RAFFINEMENT (2026-08-24, Capacitor 8 /
// API 36). Jusqu'ici `natif.js` peignait la barre de statut en vert (`#5C7A57`) et écrivait le style
// des icônes EN DUR à `DARK` — c'est-à-dire « texte clair », le bon choix sur un fond vert sombre.
//
// 🔴 Sur Android 16 (API 36), ce vert n'existe plus : le système IMPOSE l'affichage bord à bord et
// `setBackgroundColor` n'a plus aucun effet (documenté par le plugin lui-même,
// `node_modules/@capacitor/status-bar/README.md`, § « Android 16+ behavior change »). La barre
// devient TRANSPARENTE, posée par-dessus l'application. Or ce qu'il y a derrière, c'est le fond de
// `body`, donc `--bg` : en thème CLAIR, des icônes claires sur un fond blanc — **l'heure, la
// batterie et le réseau deviennent invisibles**. Sur les six fonds clairs de l'app.
//
// 📌 Une seule règle, juste dans les DEUX mondes : la couleur des icônes se déduit du fond réel.
//   · Android 16 : la barre est transparente → le fond réel est `--bg` ;
//   · Android 14 et avant : `setBackgroundColor` marche encore, et on lui donne `--bg` aussi — donc
//     le fond réel est encore `--bg`. Au passage, la barre cesse d'être verte sur les dix fonds
//     qu'elle ne s'accordait pas : elle prend la couleur de l'écran, comme partout ailleurs.
//
// ⚠️ Appelée depuis `appliquerApparence()`, le point de passage UNIQUE de l'apparence : changer de
// fond, de thème ou de taille repeint donc la barre du même geste. Aucun écran n'a à y penser.
function gfMajBarreSysteme() {
  const P = window.Capacitor && window.Capacitor.Plugins;
  const SB = P && P.StatusBar;
  if (!SB) return;   // sur le web il n'y a pas de barre système : rien à peindre, et ce n'est pas un échec
  let fond = "";
  try { fond = getComputedStyle(document.documentElement).getPropertyValue("--bg").trim(); } catch (e) {}
  if (!fond) return;
  const clair = gfCouleurClaire(fond);
  // `LIGHT` = texte SOMBRE pour un fond CLAIR ; `DARK` = texte CLAIR pour un fond SOMBRE.
  // (Relevé dans `@capacitor/status-bar/dist/esm/definitions.d.ts`, pas déduit du nom : le nom dit
  // la couleur du FOND, pas celle du texte, et se lit naturellement à l'envers.)
  if (SB.setStyle) SB.setStyle({ style: clair ? "LIGHT" : "DARK" }).catch(() => {});
  // Sans effet sur Android 16, utile en dessous. On ne teste pas la version d'Android : l'appel est
  // inoffensif là où il ne fait rien, et un test de version serait une supposition de plus.
  if (SB.setBackgroundColor) SB.setBackgroundColor({ color: fond }).catch(() => {});
}

function definirThemePerso(bgHex) {
  const r=parseInt(bgHex.slice(1,3),16), g=parseInt(bgHex.slice(3,5),16), b=parseInt(bgHex.slice(5,7),16);
  const clair = gfCouleurClaire(bgHex);
  const mix = v => Math.round(clair ? v*0.9 : v*1.15 + 10);
  const surface = "#"+[r,g,b].map(v=>Math.min(255,Math.max(0,mix(v))).toString(16).padStart(2,"0")).join("");
  const text = clair ? "#222222" : "#f2f2f2";
  const border = clair ? "#00000022" : "#ffffff22";
  localStorage.setItem("gf_theme_perso", JSON.stringify({bg:bgHex, surface, text, border}));
  choisirFond("personnalise");
}
// 🔴 CORRECTION AU PASSAGE, ET ELLE TRAÎNAIT DEPUIS LONGTEMPS. Cette fonction renvoyait
// `themeActuel()==="sombre"` : elle répondait donc NON sur « Noir », « Claude », « Abysse » et
// « Forêt », qui sont pourtant tous sombres. `teinteAdaptee()` s'en sert pour ajuster les couleurs
// de catégorie — elles étaient calculées pour un fond clair sur cinq fonds sombres sur six.
function fondEstSombre() { return FONDS_SOMBRES.includes(fondActuel()); }
function themeEstSombre() { return fondEstSombre(); }   // ancien nom, même réponse
appliquerApparence(); // appliqué au chargement de chaque page

// ---- Fond d'écran personnalisé (image, par compte) ----
/**
 * ⭐⭐ UNE SEULE FONCTION PEINT LE FOND DE `body`, ET C'EST CELLE-CI (2026-09-07).
 *
 * 🔴 POURQUOI ELLE A DÛ ÊTRE REPRISE : l'image perso s'écrivait en STYLE EN LIGNE, et le dégradé
 * devait s'écrire au même endroit. Deux fonctions qui posent `background-image` sur `body`, ce sont
 * deux fonctions qui s'effacent l'une l'autre — la dernière appelée gagne, et l'ordre d'appel n'est
 * écrit nulle part. Elles n'en font donc plus qu'une, qui compose les deux couches.
 *
 * 📌 L'ORDRE DES COUCHES EST LE SUJET : en CSS, la PREMIÈRE de la liste est celle du DESSUS. Le
 * dégradé passe donc devant l'image — c'est exactement sa demande, « le dégradé par-dessus l'image,
 * en voile ». Et comme il est écrit en `rgba(…)`, l'image reste visible dessous.
 * ⚠️ `background-attachment:fixed` est posé dès qu'il y a quelque chose à peindre : sans lui, le
 * voile défilerait avec la page et se lirait comme un objet, pas comme une lumière.
 */
/**
 * ⭐⭐⭐ LA CLARTÉ D'UNE IMAGE DE FOND, MESURÉE (2.32.3).
 *
 * Elle réduit l'image à 16 × 16 et rend la LUMINANCE PERÇUE moyenne (0 = noir, 1 = blanc) — la formule de
 * la perception (0,2126 R + 0,7152 V + 0,0722 B), pas la moyenne des trois canaux : l'œil voit le vert
 * trois fois plus que le bleu, et une photo « bleu nuit » passerait pour claire avec une moyenne bête.
 * ⛔ SANS RÉSEAU NI ATTENTE VISIBLE : l'image est déjà en mémoire (c'est une `data:` URL ou un fichier
 * local), et 256 pixels se lisent en une fraction de milliseconde.
 * ⚠️ Elle peut ÉCHOUER — canvas bloqué, image non encore décodée : on rend alors `null`, et l'appelant
 * ne touche à rien. Deviner serait pire que ne rien faire : on retomberait sur du texte illisible en
 * croyant l'avoir corrigé.
 */
function clarteImage(url) {
  return new Promise(function (resolve) {
    try {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = function () {
        try {
          const c = document.createElement("canvas");
          c.width = 16; c.height = 16;
          const ctx = c.getContext("2d", { willReadFrequently: true });
          ctx.drawImage(img, 0, 0, 16, 16);
          const d = ctx.getImageData(0, 0, 16, 16).data;
          let somme = 0, n = 0;
          for (let i = 0; i < d.length; i += 4) {
            const lin = function (v) { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); };
            somme += 0.2126 * lin(d[i]) + 0.7152 * lin(d[i + 1]) + 0.0722 * lin(d[i + 2]);
            n++;
          }
          resolve(n ? somme / n : null);
        } catch (e) { resolve(null); }
      };
      img.onerror = function () { resolve(null); };
      img.src = url;
    } catch (e) { resolve(null); }
  });
}
/**
 * Pose (ou retire) l'état « image de fond claire / sombre » sur `<html>`.
 * ⛔ `data-fond-image` PLUTÔT QU'UNE CLASSE `fond-…` : les classes `fond-x` sont retirées et reposées à
 * chaque `appliquerApparence()`, et elles portent des JEUX DE JETONS complets. Ici on ne veut qu'UNE chose
 * — dire si ce qu'il y a derrière est clair ou sombre — et la laisser survivre au reste.
 * ⚠️ Le seuil est 0,42 et pas 0,5 : le texte clair tient mieux sur un fond moyen que le texte foncé, et
 * une photo est presque toujours plus contrastée que la moyenne qu'on vient de calculer.
 */
function majClarteFondPerso(url) {
  const r = document.documentElement;
  if (!url) { delete r.dataset.fondImage; return; }
  clarteImage(url).then(function (c) {
    if (c == null) { delete r.dataset.fondImage; return; }
    r.dataset.fondImage = (c < 0.42) ? "sombre" : "clair";
  });
}
function appliquerFondPerso(url) {
  // ⚠️ L'URL EST MÉMORISÉE SUR `body`, et c'est indispensable : `appliquerApparence()` doit pouvoir
  // REPEINDRE les deux couches quand seul le dégradé change, sans avoir à retrouver l'image — qui
  // vient du COMPTE (`user_metadata.fond_url`), donc du réseau, donc pas d'ici.
  // ⛔ `undefined` ne veut PAS dire « pas d'image » : il veut dire « on ne me le dit pas cette
  // fois-ci ». Seul `null` ou `""` retire l'image.
  if (url !== undefined) {
    if (url) document.body.dataset.fondPerso = url;
    else delete document.body.dataset.fondPerso;
  }
  url = document.body.dataset.fondPerso || "";
  const deg = cssDegrade(degradeActuel());
  document.documentElement.style.setProperty("--vivant-intensite", String(opaciteVivant(intensiteVivant())));

  // ⭐⭐⭐ LE DÉGRADÉ VIT SUR SA PROPRE COUCHE, ET IL BOUGE (2026-09-08, son image 4 : « il s'agit
  // surtout d'une espèce de dégradé oui, mais EN MOUVEMENT, ce n'est surtout pas statique »).
  //
  // ⛔ POURQUOI UNE COUCHE À PART ET PAS UNE ANIMATION SUR `body`. Animer `background-position` d'un
  // fond `fixed` repeint TOUT LE VIEWPORT à chaque image — soixante fois par seconde, sur un
  // téléphone, pour un voile qu'on remarque à peine. Une couche qu'on déplace en `transform` est
  // traitée par le COMPOSITEUR : elle ne repeint rien du tout. C'est la différence entre « ça bouge »
  // et « ça rame », et elle ne se voit qu'à l'usage.
  //
  // 📌 ET ELLE SE GLISSE ENTRE L'IMAGE ET L'APPLICATION, SANS RIEN CASSER. Un enfant de `body` en
  // `z-index:-1` se peint APRÈS le fond de `body` (donc par-dessus l'image personnelle) et AVANT le
  // contenu : exactement la place qu'occupait le voile quand il était une couche de
  // `background-image`. L'ordre visuel est identique, la façon de le peindre a changé.
  // ⚠️ `inset:-30%` : la couche déborde de l'écran, sinon le déplacement découvrirait un bord.
  let voile = document.getElementById("gf-voile-degrade");
  if (deg) {
    if (!voile) {
      voile = document.createElement("div");
      voile.id = "gf-voile-degrade";
      voile.setAttribute("aria-hidden", "true");
      document.body.appendChild(voile);
    }
    // ⛔⛔ PLUS DE DÉGRADÉ PEINT SOUS LES LUMIÈRES (2.31.169, sa demande du 2026-09-16 : « j'ai essayé
    // un fond vivant et j'ai cliqué sur Obtenir, ça m'a mis les anciens affichages des dégradés, donc
    // retire-les définitivement dans l'application, c'est relou que ça revienne quelque part »).
    //
    // 🔴 CE QUE C'ÉTAIT, ET POURQUOI IL LE REVOYAIT PARTOUT : le « fond vivant » a été construit EN DEUX
    // COUCHES — l'ancien dégradé plein (`background-image: deg`, l'aplat de 2.31.98) PLUS les trois
    // lumières qui dérivent (2.31.165). La seconde était la nouveauté ; la première n'a jamais été
    // retirée. Poser un fond vivant reteintait donc tout l'écran d'un aplat figé, et c'est cet aplat
    // qu'il reconnaissait comme « les anciens dégradés ».
    // ✅ IL NE RESTE QUE LES LUMIÈRES. La couche existe toujours (elle les porte, et elle garde les trois
    // bulles hors du compte des éléments `fixed` — cf. `scripts/couches.mjs`), mais elle est TRANSPARENTE.
    // ⚠️ `deg` SERT ENCORE, ET IL LE FAUT : c'est de lui qu'on tire les teintes des trois lumières, six
    // lignes plus bas. Le catalogue des fonds vivants reste donc écrit en dégradés — c'est sa façon de
    // porter trois couleurs, pas une façon de les peindre.
    voile.style.backgroundImage = "";
    // ⭐⭐⭐ LES BULLES — « COMME UN TABLEAU SUBMERGÉ DANS L'EAU » (2.31.134, sa réponse du
    // 2026-09-13, mot pour mot) :
    //
    // > « je ne veux pas que ce soit un simple dégradé, je veux qu'il ait un fond qui bouge, que ce
    // > soit un peu + vivant, comme un tableau était submergé dans l'eau, et qu'il était possible de
    // > voir les bulles bouger, l'eau respirer »
    //
    // 🔴 CE QU'IL Y AVAIT : un voile d'une seule pièce qui grandissait de 6 % en **48 secondes**.
    // Mathématiquement en mouvement, humainement immobile — un écran regardé dix secondes ne montrait
    // rien du tout. Et son intensité venait d'être divisée par trois (2.31.98) : ni la couleur ni le
    // mouvement n'étaient perceptibles. D'où ses deux phrases du même jour, « y a rien » et « il faut
    // que ce soit une animation en fond ».
    //
    // ✅ TROIS TACHES DOUCES DÉRIVENT MAINTENANT DANS LE VOILE, chacune à sa vitesse et sur son
    // chemin, désynchronisées par des retards NÉGATIFS (elles sont déjà en route quand l'écran
    // s'ouvre — sinon les trois partiraient du même point, ce qui se lit comme un clignotement).
    //
    // ⛔⛔ ELLES VIVENT **DANS** LE VOILE, JAMAIS À CÔTÉ, ET C'EST UNE CONTRAINTE MESURÉE :
    // `node scripts/couches.mjs` plafonne à **4 éléments `fixed`** (il y en a 3), parce que c'est
    // l'instrument du clignotement sur Android — 28 couches avant correction, 3 après. Trois bulles
    // posées en `position:fixed` auraient fait passer le compte à 6 et rouvert un défaut qui a coûté
    // une version entière. Enfants d'un parent `fixed`, elles n'en ajoutent aucun.
    //
    // 📌 LA COULEUR VIENT DU DÉGRADÉ LUI-MÊME, jamais d'une palette écrite ici : chaque dégradé garde
    // son identité, et un dégradé ajouté demain a ses bulles sans qu'on touche à cette fonction.
    // ⚠️ `transform` et `opacity` UNIQUEMENT — les deux seules propriétés que le compositeur traite
    // sans repeindre. C'est la raison d'être de toute cette couche (voir le commentaire du dessus) :
    // animer autre chose repeindrait l'écran soixante fois par seconde pour un voile.
    const teintes = [];
    String(deg).replace(/rgba\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)/g, function (m, r, v, b) {
      const t = r + "," + v + "," + b;
      if (teintes.indexOf(t) < 0) teintes.push(t);
      return m;
    });
    // ⭐⭐⭐ « VOILAGE » — SON CHOIX DU 2026-09-14, sur les quatre propositions rendues en plein écran
    // (Phares, Braise, Voilage, Bulles) : « Voilage ça a l'air bien, mais faut pas que ce soit aussi
    // flagrant, il faut simplement que ce soit partiellement visible ».
    // ✅ DEUX GRANDS VOILES QUI TOURNENT LENTEMENT, en sens inverse, sur plus d'une minute chacun (64 et
    // 91 s, premiers entre eux : le motif ne se répète pas). Aucun début ni fin : la lumière change en
    // permanence sans qu'on puisse dire à quel moment — c'est ce qui le distinguait des trois autres.
    // ⚠️ ET À DEMI-TEINTE, C'EST SA CONSIGNE : les alphas de la proposition (.34 / .13) sont divisés par
    // deux (.18 / .07). « Partiellement visible » — on doit pouvoir douter qu'il bouge, pas le subir.
    // ⛔ Les trois bulles du 2.31.134 (« comme un tableau submergé dans l'eau ») sont remplacées, pas
    // empilées : il les a vues et les a refusées (« c'est pas ce genre d'animation que je veux »).
    // ⭐⭐⭐ « FOND VIVANT » — TROIS PETITES LUMIÈRES (2.31.165, sa demande du 15/09 : « des petites
    // lumières qui bougent […] tu n'as rien changé »). Le Voilage à demi-teinte (.18) a été réglé pour
    // qu'on puisse douter qu'il bouge, et il en a douté. Trois disques flous, cœur à .38, en orbite
    // (voir app.css `gfLumiereA/B/C`) : le dessin du fond du site, qu'il a accepté en 2.31.163.
    const lumiere = (t) => "radial-gradient(closest-side, rgba(" + t + ",.38) 0%, rgba(" + t + ",.16) 48%, rgba(" + t + ",0) 76%)";
    if (!voile.firstChild && teintes.length) {
      for (let k = 0; k < 3; k++) {
        const bulle = document.createElement("i");
        bulle.className = "gf-bulle gf-bulle-" + (k + 1);
        bulle.style.backgroundImage = lumiere(teintes[k % teintes.length]);
        voile.appendChild(bulle);
      }
    } else if (voile.firstChild && teintes.length) {
      // Changement de dégradé : on reteinte les lumières existantes plutôt que de les recréer — les
      // recréer relancerait les trois animations au même instant, donc un saut visible.
      const b = voile.children;
      for (let k = 0; k < b.length; k++) b[k].style.backgroundImage = lumiere(teintes[k % teintes.length]);
    }
  } else if (voile) {
    // ⛔ On RETIRE la couche plutôt que de la vider : un élément fixe qui couvre l'écran, même
    // transparent, reste un élément que le compositeur transporte à chaque défilement.
    voile.remove();
  }

  const couches = [];
  if (url) couches.push("url('" + url + "')");
  if (!couches.length) { document.body.style.backgroundImage = ""; }
  else {
    document.body.style.backgroundImage = couches.join(", ");
    document.body.style.backgroundSize = "cover";
    document.body.style.backgroundPosition = "center";
    document.body.style.backgroundAttachment = "fixed";
  }
  // ⭐⭐⭐ LE TEXTE S'ADAPTE À L'IMAGE (2.32.3, sa demande : « il faudrait que la couleur de la police de
  // tous les textes s'adapte en fonction du FOND, pour ressortir les polices »).
  //
  // 📌 SON CHOIX, POSÉ EN QUESTION : « bascule + voile derrière les textes ». Les deux, parce qu'AUCUN des
  // deux ne suffit seul sur une photo : une photo n'a pas UNE clarté, elle en a autant que de zones. Le
  // texte clair sur un ciel blanc reste illisible quelle que soit la bascule ; et un voile seul, sur une
  // photo sombre, laisserait du texte foncé sur du foncé.
  // ⛔ CE QUI EXISTAIT DÉJÀ, ET CE QUI MANQUAIT : les FONDS livrés savent s'ils sont sombres
  // (`FONDS_SOMBRES`), et l'app bascule ses jetons en conséquence. Une image PERSONNELLE, elle, n'était
  // dans aucune liste — les jetons restaient ceux du fond choisi avant, et une photo sombre gardait du
  // texte noir. C'est exactement ce qu'il décrit.
  // ⚠️ ON MESURE L'IMAGE, ON NE DEVINE PAS. `clarteImage()` la réduit à 16 × 16 et fait la moyenne
  // pondérée (la luminance perçue, pas la moyenne des trois canaux). Une photo n'est pas « claire parce
  // qu'elle est blanche en haut ».
  majClarteFondPerso(url);
  // ⭐ L'apparence suit le compte (2.31.165) : l'app, si elle est là, compare et enregistre.
  if (typeof window.gfApparenceAppliquee === "function") { try { window.gfApparenceAppliquee(); } catch (e) {} }
}

// ---- Notification passagère (« toast »), partagée par toutes les pages ----
// Sa demande du 2026-08-10 : une confirmation ne doit pas s'écrire en bloc au milieu du formulaire,
// elle doit passer comme la barre « Annuler » d'une suppression. Le style vit dans styles.css.
// ⚠️ Les ERREURS aussi, depuis le 2026-08-10 : la version précédente les gardait écrites à côté du
// champ « pour pouvoir être relues », et il a tranché le contraire, capture à l'appui (« Code
// incorrect, réessaie. » en bloc rouge sous le bouton 2FA). Passer `type:"err"` colore le toast et
// double sa durée d'affichage — c'est ce qui remplace la permanence du bloc.
// `texte` est toujours du texte brut (textContent, jamais innerHTML) : ces messages reprennent parfois
// un pseudo ou une adresse saisis par l'utilisateur.
//
// ⭐⭐⭐ 2026-08-16 — CETTE FONCTION EST LE SEUL ENDROIT OÙ L'APPLICATION DIT « C'EST FAIT ».
// Sa demande, mot pour mot, capture à l'appui (« Corbeille vidée. » en bloc vert au milieu des
// Paramètres) : « les messages verts comme ça doivent apparaître en tant que notification en bas de
// l'écran, appliquer à tout sur l'application ». Les 27 blocs `.message` qui vivaient dans les
// formulaires sont donc tous passés par ici, y compris ceux de `profil.html` et d'`index.html`.
// 🔴 CE QU'IL NE FAUT PLUS ÉCRIRE, NULLE PART, ET LA RÈGLE VAUT POUR TOUT AJOUT FUTUR :
//     el.innerHTML = '<div class="message ok">…</div>'
//   Un compte rendu de geste (réussite, échec, avertissement) part en toast. Sans exception.
// 📌 LES DEUX SEULS BLOCS QUI RESTENT ÉCRITS SUR PLACE, et ce ne sont pas des comptes rendus :
//   · `#reset-avertissement` (profil.html) — l'avertissement AVANT de réinitialiser, qui doit rester
//     sous les yeux pendant qu'on décide. Un texte qui s'efface au bout de 6 s ne sait pas faire ça.
//   · `#recu-conflit` et consorts — les états d'un formulaire, pas le résultat d'une action.
//
// 📌 UN TOAST EN REMPLACE UN AUTRE INSTANTANÉMENT (même élément, minuteur remis à zéro). C'est ce
// qui permet d'annoncer une attente puis son résultat sans laisser deux traces : `gfToast("Envoi en
// cours…", 20000)` puis `gfToast("Fond d'écran mis à jour.")` — le second chasse le premier.
// Trois niveaux : rien (confirmation), "warn" (avertissement, ambre), "err" (échec, rouge).
let _gfToastTimer = null;
// ⭐⭐⭐ LE TOAST S'EFFACE EN QUITTANT LE DOM, ET TROIS DÉCLENCHEURS INDÉPENDANTS S'EN CHARGENT
// (2026-08-18, 9e signalement du même défaut — « le problème est toujours présent »).
//
// 🔴 L'HISTORIQUE DES QUATRE REMÈDES RATÉS, parce qu'il dit ce qu'il ne faut PLUS réessayer :
//   1. (2.12.14) forcer un reflow avant la transition CSS → sans effet ;
//   2. (2.12.14) un filet à 320 ms qui imposait `transform` en dur → sans effet ;
//   3. (2.12.15) tout piloter en `element.animate()` au lieu d'une classe + transition, le remède
//      qui avait pourtant marché pour la volée de Nacres sur CE téléphone → sans effet ;
//   4. (2.12.19) une vérification d'échéance toutes les secondes, qui relançait l'animation de
//      SORTIE → sans effet.
// Le point commun des quatre : tous laissaient l'élément EN PLACE et comptaient sur une propriété
// CSS (`transform`) pour le pousser hors de l'écran. Si cette propriété n'est pas repeinte — et
// c'est le seul fait établi, observé dès la volée de Nacres sur cet appareil — le toast reste
// visible, quelle que soit la façon dont on a demandé le déplacement.
//
// ⭐ D'OÙ LE CHANGEMENT DE NATURE : on ne DÉPLACE plus le toast, on le RETIRE du document
// (`el.remove()`). Un élément qui n'existe plus ne peut pas être peint — aucune couche de
// composition, aucun `transform` figé, aucun cache de rendu ne peut le maintenir à l'écran. C'est la
// seule fermeture qui ne repose sur aucune hypothèse de repeinte. `gfToast()` le recrée à l'appel
// suivant : il n'y a donc rien à remettre en place.
//
// ⭐ ET TROIS DÉCLENCHEURS QUI NE DÉPENDENT PAS LES UNS DES AUTRES, parce que l'autre hypothèse
// encore ouverte est que les minuteurs eux-mêmes se figent (WebView Android mise en veille) :
//   · le `setTimeout` normal — le chemin qui joue l'animation de sortie ;
//   · un `setInterval` d'une seconde — si le `setTimeout` a glissé ;
//   · **le moindre appui à l'écran, et le retour au premier plan** — les seuls qui ne supposent AUCUN
//     minuteur vivant. C'est le déclencheur qui compte vraiment ici : sur ses trois captures, il
//     naviguait dans l'app pendant que le toast restait affiché. Chaque geste le balaie maintenant.
// ⚠️ Les trois passent par la MÊME fonction, avec la même échéance : impossible qu'ils se
// contredisent, et un toast frais ne peut pas être tué par un appui (délai de grâce ci-dessous).
let _gfToastEcheance = 0;
/**
 * ⭐⭐ LA DISTANCE DE SORTIE SE MESURE, ELLE NE S'ECRIT PAS EN POURCENTAGE (2026-08-19, son 10e
 * signalement sur ce toast — et cette fois le defaut a ete REPRODUIT au banc, chiffres a l'appui).
 *
 * 🔴 CE QU'IL DECRIVAIT : « la notification apparait, puis quand le delai est ecoule ca glisse vers
 * le bas, puis ca prend environ 1 seconde a disparaitre APRES que l'animation soit finie ». Mesure au
 * banc, ecran de 915 px : a la fin du glissement, le haut du toast est a 874 px — donc **41 px de la
 * notification restent a l'ecran**, immobiles, jusqu'a ce que `el.remove()` finisse par tomber.
 *
 * 🔴 LA CAUSE, et elle etait sous les yeux depuis le debut : `translateY(160%)` deplace l'element de
 * 160 % de SA PROPRE HAUTEUR (45 px x 1,6 = 72 px). Or il faut franchir sa hauteur PLUS sa distance au
 * bas de l'ecran (`bottom: --barre-onglets + 12px`), soit 113 px ici. Un pourcentage ne peut pas
 * connaitre cette seconde moitie : il est faux des que la barre d'onglets ou la hauteur du texte
 * changent — c'est-a-dire toujours, et pas seulement sur son telephone.
 *
 * ⭐ D'OU CE CALCUL : on demande au navigateur ou est le toast, et on le pousse jusqu'a ce que son
 * HAUT passe sous le bas de l'ecran, plus 24 px de marge. La notification est alors invisible A
 * L'INSTANT ou l'animation finit — et le retrait du DOM qui suit, quel que soit son retard, ne se voit
 * plus. C'est exactement le repli qu'il proposait : « au pire des cas fais en sorte que le glissement
 * se fasse plus bas, en sorte que l'user ne le voie pas, et la peu importe les delais ».
 * ⚠️ Ne jamais revenir a un pourcentage. `translateY(160%)` reste dans styles.css comme repli du tout
 * premier affichage (avant le premier appel), la ou aucune mesure n'est encore possible.
 */
function _gfToastDistanceSortie(el){
  try{
    // 🔴 ON NEUTRALISE LA TRANSFORMATION AVANT DE MESURER, et ce n'est pas une precaution : sans ca, la
    // premiere version de ce correctif rendait 65 px au lieu de 137, et 44 px du toast restaient a
    // l'ecran — le defaut d'origine, a peine attenue. La raison : `_gfToastFermer()` annule les
    // animations JUSTE AVANT d'appeler cette fonction, et l'element retombe alors sur le
    // `transform:translateY(160%)` de styles.css (le repli du premier affichage). On mesurait donc un
    // element DEJA descendu de 72 px, et on calculait le reste du chemin a partir de la.
    // ⚠️ Mesurer un element anime, c'est mesurer un etat de passage. Toujours neutraliser d'abord.
    const avant = el.style.transform;
    el.style.transform = "none";
    const r = el.getBoundingClientRect();
    el.style.transform = avant;
    const h = (window.visualViewport && window.visualViewport.height) || window.innerHeight || 0;
    // Au minimum sa propre hauteur : pour que la sortie reste un vrai mouvement, meme si la mesure
    // devait echouer et rendre un `top` aberrant.
    return Math.max(r.height + 24, Math.round(h - r.top) + 24);
  }catch(e){ return 400; }   // un repli genereux vaut mieux qu'un toast coince a l'ecran
}
/** Retire le toast du document, sans animation. Le dernier recours, et le seul infaillible. */
function _gfToastRetirer(){
  _gfToastEcheance = 0;
  clearTimeout(_gfToastTimer); _gfToastTimer = null;
  const el = document.getElementById("gf-toast");
  if(el) el.remove();
}
/** La fermeture normale : on joue la sortie, puis on retire. Le retrait est doublé d'un filet. */
function _gfToastFermer(){
  const el = document.getElementById("gf-toast");
  if(!el){ _gfToastEcheance = 0; return; }
  _gfToastEcheance = 0;
  clearTimeout(_gfToastTimer); _gfToastTimer = null;
  el.getAnimations().forEach(a => a.cancel());
  const jauge = el.querySelector(".jauge");
  if(jauge) jauge.getAnimations().forEach(a => a.cancel());
  const dy = _gfToastDistanceSortie(el);
  const sortie = el.animate(
    [{ transform: "translateY(0)" }, { transform: "translateY(" + dy + "px)" }],
    { duration: 200, easing: "cubic-bezier(.22,.7,.32,1)", fill: "forwards" }
  );
  // ⚠️ DEUX CHEMINS VERS LE RETRAIT, et c'est voulu : `onfinish` ne se déclenche pas si l'animation
  // n'est jamais jouée (exactement le défaut qu'on corrige), le `setTimeout` retire alors quand même.
  // 🔴 LE `_gfToastEcheance` À ZÉRO EST LA CONDITION, PAS UNE PRÉCAUTION DÉCORATIVE : un nouveau toast
  // appelé pendant ces 400 ms réutilise LE MÊME élément et réarme l'échéance dans le futur. Sans ce
  // test, le retrait en retard emporterait le toast SUIVANT, qui vient à peine d'apparaître —
  // « Envoi en cours… » puis « C'est envoyé. » se serait effacé au bout de 300 ms.
  const retirer = () => { if(_gfToastEcheance === 0 && el.parentNode) el.remove(); };
  sortie.onfinish = retirer;
  setTimeout(retirer, 400);
}
/**
 * Le toast affiché a-t-il dépassé son échéance ? Si oui, on le retire sur-le-champ.
 * ⚠️ 600 ms de grâce : ça laisse au chemin normal (animation de sortie) le temps de faire son travail
 * proprement. Sans cette marge, un appui pile à l'échéance escamoterait l'animation pour rien.
 */
function _gfToastPerime(){
  if(_gfToastEcheance && Date.now() >= _gfToastEcheance + 600) _gfToastRetirer();
}
// ⚠️ En capture (`true`) et en passif : on observe l'appui sans jamais l'intercepter — aucun bouton
// de l'app ne doit changer de comportement parce qu'un toast traîne.
document.addEventListener("pointerdown", _gfToastPerime, { capture: true, passive: true });
document.addEventListener("visibilitychange", _gfToastPerime);
const _gfToastVeille = setInterval(_gfToastPerime, 1000);
// Sous Node (les suites de test exécutent ce fichier tel quel, dans un vrai contexte `vm`), un
// intervalle actif empêche le processus de jamais se terminer. Un navigateur rend un simple nombre,
// sans `unref` : le `typeof` protège cet appel-là, il ne change rien en dehors des tests.
if(_gfToastVeille && typeof _gfToastVeille.unref==="function") _gfToastVeille.unref();
// ⭐⭐⭐ PILOTÉ EN JAVASCRIPT (`element.animate()`), PLUS EN CLASSE + TRANSITION CSS (2026-08-18,
// 7e signalement du même défaut : « la notification reste bloquée en bas, tout le temps »).
// Deux correctifs précédents (forcer un reflow, puis un filet qui imposait `transform` en dur) sont
// restés sans effet sur son téléphone, confirmé après vérification qu'il testait bien la dernière
// version et qu'il regardait l'écran plusieurs dizaines de secondes — pas un simple effet de capture
// prise trop tôt. Pendant ce temps, `celebrerNacres()` (morceau 16) a eu le défaut SYMÉTRIQUE (la
// volée de Nacres ne semblait pas bouger) et son remède — piloter le mouvement avec `element.animate()`
// au lieu d'une classe + transition — a été confirmé « parfaite » sur ce même téléphone. Le point
// commun : classe CSS + transition implicite ne se repeint pas de façon fiable sur cet appareil ;
// `element.animate()`, si.
// ⭐⭐ LA DURÉE SUIT LA LONGUEUR DU TEXTE (2026-08-20, sa demande) : « les messages qui dépassent un
// certain nombre de caractères, il faut que la durée de notification s'adapte au nombre de mots, par
// exemple 0,2 s par mot supplémentaire, afin de donner le temps aux utilisateurs de lire ».
//
// 🔴 POURQUOI 3 SECONDES NE SUFFISAIENT PLUS. Ce plafond datait du jour où les toasts tenaient en
// quatre mots (« Photo de profil mise à jour. »). Depuis, plusieurs disent une vraie phrase — le
// compte rendu d'une liaison de comptes en fait quarante. Trois secondes pour quarante mots, ce n'est
// pas une notification, c'est un texte qu'on n'a pas le temps de lire.
//
// ⚠️ TROIS BORNES, ET CHACUNE A SA RAISON :
//   · les 8 premiers mots sont gratuits — en dessous, un toast se lit d'un coup d'œil, et rallonger
//     un message court le ferait seulement traîner ;
//   · +200 ms par mot au-delà, sa valeur, qui correspond à une lecture posée ;
//   · 15 s au plus. Sans plafond, un message très long resterait à l'écran assez longtemps pour
//     devenir le défaut qu'il a signalé neuf fois (« la notification reste bloquée en bas »). Ce qui
//     mérite plus de 15 s mérite une fenêtre, pas un toast.
function gfDureeToast(texte) {
  const mots = String(texte || "").trim().split(/\s+/).filter(Boolean).length;
  return Math.min(15000, 3000 + Math.max(0, mots - 8) * 200);
}
// ⭐ Le mode d'affichage des notifications, réglé dans Plus › Options.
// 📌 EN `localStorage`, PAS DANS LE CONTENU DU COMPTE, et c'est délibéré : `config.js` est partagé par
// TOUTES les pages, y compris la connexion et la création de compte, qui ne chargent ni `app.js` ni la
// moindre donnée de compte. Une préférence d'affichage qui ne vaudrait pas sur ces pages-là serait une
// demi-préférence. C'est le même choix que le thème, rangé au même endroit pour la même raison.
function gfToastSansDelai() {
  try { return localStorage.getItem("gf_toast_mode") === "croix"; } catch (e) { return false; }
}

/**
 * ⭐⭐⭐ ON POUSSE UNE NOTIFICATION DU DOIGT POUR LA FAIRE PARTIR (2.32.39, sa demande du 2026-09-21 :
 * « donne la possibilité de toucher la notification qui affiche en bas, et de pouvoir la déplacer
 * dans les 4 directions pour les faire disparaître, exactement ce qu'on ferait pour une notification
 * Android »). Les quatre directions, bas compris — son choix, questionné le même jour.
 *
 * ⛔ ET SEULEMENT SI LE DOIGT EST POSÉ SUR ELLE. Sa consigne, mot pour mot : « mais uniquement si on
 * a posé le doigt sur la notification qui affiche en bas, c'est tout, ça ne doit impacter rien
 * d'autre de l'application ». D'où `pointerdown` sur l'élément lui-même — jamais sur le document —
 * et `setPointerCapture`, qui garde le geste même si le doigt sort de la notification en glissant.
 *
 * ⚠️ `#gf-toast` EST EN `pointer-events:none` PAR DÉFAUT : il ne doit jamais voler un appui destiné à
 * l'écran du dessous. On le rend touchable ici, et on le remet inerte dès que le geste est fini.
 * ⛔ 40 px AVANT DE DÉCIDER : sous ce seuil, le doigt revient en place et le toast reprend sa minuterie.
 * Un simple effleurement ne doit pas effacer ce qu'on n'a pas fini de lire.
 */
function gfToastGlissable(el, fermer) {
  if (!el || el.dataset.glissable) return;
  el.dataset.glissable = "1";
  // ⭐ LE MÊME GESTE POUR LES TROIS NOTIFICATIONS (2.32.39, son choix du 2026-09-21 : « les deux,
  // même geste »). Ce qui change d'une à l'autre, c'est seulement COMMENT on la fait partir : le
  // toast du bas a `_gfToastRetirer`, les barres de droite retirent leur classe `on`. On passe donc
  // la fermeture en paramètre plutôt que d'écrire trois fois le même glissement.
  const partirPourDeBon = (typeof fermer === "function") ? fermer : function(){ try { _gfToastRetirer(); } catch (e) {} };
  // ⚠️ `touch-action:none` VIENT D'ICI, PAS DE CHAQUE FEUILLE DE STYLE : sans lui, le navigateur
  // décide que le doigt qui descend veut faire défiler la page, garde le geste pour lui, et nos
  // `pointermove` s'arrêtent net au bout de quelques pixels. La règle est dans `styles.css`, que les
  // trois pages chargent.
  el.classList.add("gf-glissable");
  let x0 = 0, y0 = 0, dx = 0, dy = 0, actif = false, base = "";
  // ⭐⭐⭐ LA NOTIFICATION SUIT VRAIMENT LE DOIGT (2.32.40, sa remontée du 2026-09-21 : « ce que j'ai
  // fait sur le fait de glisser les notifications, il faut avoir l'animation de comme si on prenait la
  // notification et qu'on la déplaçait, refais-le »).
  //
  // 🔴 CE QUI SE PASSAIT, ET IL Y AVAIT DEUX CAUSES SUPERPOSÉES — d'où le fait que la première
  // écriture semblait pourtant juste :
  //   ① LE TOAST DU BAS NE BOUGEAIT PAS D'UN PIXEL. Son entrée est jouée par `el.animate(...)` avec
  //      `fill:"forwards"`, et une animation qui tient sa valeur PRIME SUR LE STYLE EN LIGNE. On avait
  //      beau écrire `el.style.transform` à chaque `pointermove`, l'animation le recouvrait. Le doigt
  //      glissait, la notification restait plantée, et elle sautait d'un coup au relâchement.
  //   ② LES BARRES DE DROITE TRAÎNAIENT DERRIÈRE LE DOIGT. `el.style.transition = ""` retire la
  //      transition EN LIGNE — pas celle que leur classe CSS leur donne (`.solde-flottant` a une
  //      transition sur `transform`). Chaque position passait donc par une interpolation de plusieurs
  //      centaines de millisecondes : on ne déplaçait pas la notification, on la tirait à l'élastique.
  //
  // ✅ ON FIGE D'ABORD, ON DÉPLACE ENSUITE. Au premier contact, on relève la position RÉELLE (matrice
  // calculée, animation comprise), on la pose en ligne, et on annule ce qui la produisait. À partir de
  // là, l'élément n'obéit plus qu'à nous, et chaque `pointermove` se voit à l'image près.
  const figer = () => {
    let t = "";
    try {
      const enCours = el.getAnimations ? el.getAnimations() : [];
      if (enCours.length) {
        // ⚠️ DANS CET ORDRE, ET C'EST TOUT LE CORRECTIF : on LIT la matrice pendant que l'animation la
        // tient encore, puis seulement on annule. L'inverse ferait retomber le toast sur son repli de
        // ⚠️ Le repli en question est écrit dans `styles.css`, sur `#gf-toast` : il le place hors de
        // l'écran, sous le bord bas. Il n'est pas recopié ici — `tests/notifications.test.js` refuse
        // toute distance en pourcentage dans une animation du toast, et il a raison de le faire : un
        // pourcentage ne connaît que la hauteur du toast, jamais sa distance au bas de l'écran.
        // `styles.css` le ramènerait donc hors champ le temps d'une image — un clignotement.
        t = getComputedStyle(el).transform;
        if (t === "none") t = "";
        enCours.forEach(a => { try { a.cancel(); } catch (err) {} });
        el.style.transform = t;
      }
    } catch (e) {}
    return t;
  };
  // Le déplacement se COMPOSE avec la position figée au lieu de la remplacer : sans ça, une barre
  // ancrée au bord droit repartirait du centre de l'écran dès le premier pixel de glissement.
  const poser = (mouvement) => { el.style.transform = base ? (base + " " + mouvement) : mouvement; };
  const douceur = () => { try { return !matchMedia("(prefers-reduced-motion: reduce)").matches; } catch (e) { return true; } };
  const fini = (partir) => {
    el.classList.remove("gf-prise");
    el.style.transition = "transform .2s ease, opacity .2s ease";
    if (partir) {
      const loin = Math.abs(dx) > Math.abs(dy);
      const tx = loin ? (dx > 0 ? innerWidth : -innerWidth) : 0;
      const ty = loin ? 0 : (dy > 0 ? 260 : -260);
      poser("translate(" + tx + "px," + ty + "px)");
      el.style.opacity = "0";
      setTimeout(() => { el.style.transition = ""; el.style.transform = ""; el.style.opacity = "";
                         try { partirPourDeBon(); } catch (e) {} }, 200);
    } else {
      // Retour à sa place : on repasse par `poser`, jamais par une remise à zéro sèche.
      poser("translate(0px,0px)");
      el.style.opacity = "";
      setTimeout(() => {
        el.style.transition = "";
        // ⛔ ON NE REND LA MAIN AU CSS QUE SI ON NE LUI DOIT RIEN. Quand `base` est vide, l'élément
        // tenait sa position de sa propre classe : effacer le style en ligne la lui rend, et sa
        // fermeture normale (retrait de `.on`) refonctionne. Quand `base` porte une matrice, elle
        // vient d'une animation qu'on a annulée — l'effacer ferait tomber le toast hors de l'écran.
        if (!base) el.style.transform = "";
      }, 220);
    }
    actif = false;
  };
  el.addEventListener("pointerdown", (e) => {
    actif = true; x0 = e.clientX; y0 = e.clientY; dx = 0; dy = 0;
    el.style.transition = "none";     // « none », pas "" : "" laisserait la transition de la CLASSE agir
    base = figer();
    el.classList.add("gf-prise");
    try { el.setPointerCapture(e.pointerId); } catch (err) {}
  });
  el.addEventListener("pointermove", (e) => {
    if (!actif) return;
    dx = e.clientX - x0; dy = e.clientY - y0;
    // ⭐ LES DEUX AXES EN MÊME TEMPS (et non plus le seul axe dominant) : c'est ce qui fait la
    // différence entre « on la pousse sur un rail » et « on la tient ». Le DÉPART, lui, reste décidé
    // par l'axe dominant au relâchement — on ne peut pas partir en diagonale.
    // Le léger rétrécissement est le « on la soulève » qu'il décrit ; il disparaît si la personne a
    // demandé moins d'animations à son téléphone.
    poser("translate(" + dx + "px," + dy + "px)" + (douceur() ? " scale(.985)" : ""));
    el.style.opacity = String(Math.max(0.25, 1 - Math.max(Math.abs(dx), Math.abs(dy)) / 220));
  });
  const relacher = () => { if (actif) fini(Math.max(Math.abs(dx), Math.abs(dy)) > 40); };
  el.addEventListener("pointerup", relacher);
  el.addEventListener("pointercancel", relacher);
}

try { window.gfGlissablePourFermer = gfToastGlissable; } catch (e) {}

function gfToast(texte, duree, type) {
  let el = document.getElementById("gf-toast");
  if (!el) {
    el = document.createElement("div");
    el.id = "gf-toast";
    el.setAttribute("role", "status");      // lu par les lecteurs d'écran sans voler le focus
    el.setAttribute("aria-live", "polite");
    // La jauge verte (2026-08-17, sa demande : « il doit avoir une barre verte, comme lorsqu'on
    // rentre une dépense, qui signale la durée de disparition »).
    const jauge = document.createElement("span");
    jauge.className = "jauge";
    el.appendChild(document.createElement("span")).className = "txt";
    el.appendChild(jauge);
    document.body.appendChild(el);
    gfToastGlissable(el);
  }
  el.querySelector(".txt").textContent = texte || "";
  el.classList.toggle("err", type === "err");
  el.classList.toggle("warn", type === "warn");
  // ⭐⭐ LE MODE « SANS DÉLAI » (2026-08-20, sa demande) : « ajoute une option dans les paramètres où
  // il est possible de changer la manière dont les notifications alertes s'affichent, il y a le délai,
  // et il y a une option sans délai où il faut appuyer sur une croix pour retirer la notification à
  // droite ». La croix vit dans le toast lui-même : elle n'a rien à faire ailleurs, et elle ne coûte
  // rien tant que le mode n'est pas activé (`display:none`).
  const sansDelai = gfToastSansDelai();
  let croix = el.querySelector(".gf-toast-x");
  if (!croix) {
    croix = document.createElement("button");
    croix.type = "button";
    croix.className = "gf-toast-x";
    croix.setAttribute("aria-label", "Fermer la notification");
    croix.textContent = "×";
    croix.onclick = _gfToastRetirer;
    el.appendChild(croix);
  }
  croix.style.display = sansDelai ? "" : "none";
  // ⚠️ `#gf-toast` est en `pointer-events:none` (il ne doit jamais voler un appui destiné à l'écran) :
  // seul le mode « sans délai » lui rend la capacité d'être touché, et uniquement sur la croix.
  el.classList.toggle("sans-delai", sansDelai);
  el.classList.add("glissable");
  // ⭐ 3 SECONDES, C'EST TOUT (2026-08-17, sa demande : « il ne part jamais »). Réussite, erreur ou
  // avertissement suivent la même règle — un toast se relit d'un coup d'œil, il n'y a rien à laisser
  // traîner plus longtemps. Ce qui mérite plus de temps a sa propre barre : la suppression a
  // `#annuler-barre` (5 s, voir `DELAI_ANNULATION`), et au pire des cas la corbeille garde tout 7 jours.
  const attente = duree || gfDureeToast(texte);
  const jauge = el.querySelector(".jauge");
  // Un toast qui en remplace un autre encore affiché doit annuler ses animations en cours, sinon la
  // fermeture programmée par le précédent appel referme celui-ci au mauvais moment.
  el.getAnimations().forEach(a => a.cancel());
  jauge.getAnimations().forEach(a => a.cancel());
  clearTimeout(_gfToastTimer);
  // Meme distance a l'aller qu'au retour (mesuree, pas en pourcentage) : le toast entre donc bien de
  // SOUS l'ecran, et non d'un point qui se trouvait deja a moitie visible.
  el.animate(
    [{ transform: "translateY(" + _gfToastDistanceSortie(el) + "px)" }, { transform: "translateY(0)" }],
    { duration: 260, easing: "cubic-bezier(.22,.7,.32,1)", fill: "forwards" }
  );
  // En mode « sans délai », il n'y a pas de temps qui passe : la jauge n'aurait rien à montrer, et
  // une barre figée à 100 % laisserait croire à une minuterie arrêtée.
  jauge.style.display = sansDelai ? "none" : "";
  if (!sansDelai) {
    jauge.animate(
      [{ transform: "scaleX(1)" }, { transform: "scaleX(0)" }],
      { duration: attente, easing: "linear", fill: "forwards" }
    );
  }
  // `_gfToastEcheance` arme les trois déclencheurs déclarés en tête de fichier : si CE minuteur ne
  // suffit pas à faire disparaître le toast, l'intervalle, le prochain appui à l'écran ou le retour
  // au premier plan le retireront du document à sa place.
  // ⛔ `_gfToastEcheance` RESTE À ZÉRO EN MODE « SANS DÉLAI », et ce n'est pas un détail : c'est elle
  // qui arme les trois déclencheurs de fermeture automatique (minuteur, intervalle d'une seconde, et
  // le moindre appui à l'écran). À zéro, aucun des trois ne peut retirer le toast — seule la croix le
  // fera, ce qui est exactement ce qu'il a demandé.
  if (sansDelai) {
    _gfToastEcheance = 0;
    clearTimeout(_gfToastTimer); _gfToastTimer = null;
    return;
  }
  _gfToastEcheance = Date.now() + attente;
  _gfToastTimer = setTimeout(_gfToastFermer, attente);
}

// ⭐⭐⭐ L'ŒIL QUI MONTRE LE MOT DE PASSE (2.32.18, sa demande du 2026-09-18 : « il faut que tu donnes
// l'autorisation d'afficher le mot de passe lorsqu'on le tape a la page de connexion »).
//
// 📌 POURQUOI ICI, ET PAS DANS CHAQUE PAGE. Il y a DOUZE champs de mot de passe dans Solvea, répartis
// sur trois pages qui ne partagent aucun script : la connexion et le mot de passe oublié
// (`index.html`), la création de compte (`creer-compte.html`), et sept dans l'application elle-même
// (changer de mot de passe, lier une adresse, se connecter à un autre compte). Trois copies, c'est
// la garantie qu'un jour l'une des trois aura l'œil et pas les autres — l'incohérence exacte qu'il
// nous a demandé d'arrêter de produire. `config.js` est le seul fichier que les trois chargent.
//
// ⛔ CE QUI A ÉTÉ VÉRIFIÉ AVANT D'ENVELOPPER LES CHAMPS, et ce n'était pas une précaution de style :
// aucune règle de `styles.css` ni d'`app.css` ne vise un champ PAR SON VOISIN (`.champ + …`, `… ~
// .champ`). Envelopper l'input dans un `<span>` ne peut donc casser aucune mise en page. Le `span`
// est en `display:block` pour que le champ garde exactement la largeur et la marge qu'il avait.
//
// ⛔ LE TYPE REVIENT À `password` À CHAQUE FERMETURE DE PAGE — non, il ne revient pas tout seul, et
// c'est justement pourquoi le bouton se remet à zéro quand le champ se vide : un mot de passe laissé
// en clair dans un formulaire rouvert plus tard se lit par-dessus l'épaule. `autocomplete` et les
// gestionnaires de mots de passe ne sont pas touchés : on ne change QUE le `type`.
//
// ⚠️ `aria-pressed` ET UN LIBELLÉ QUI CHANGE : un lecteur d'écran doit savoir si le mot de passe est
// visible. L'icône seule ne dit rien à qui ne la voit pas.
// ⭐⭐⭐ CE QUE LA PREMIÈRE VERSION FAISAIT MAL, ET IL L'A VU EN DEUX MINUTES (2026-09-19, ses trois
// remarques) : « lorsque je clique sur l'œil ça me ferme le clavier » · « l'œil doit apparaître
// uniquement quand on commence à taper, il n'est pas là par défaut, et quand c'est vide il doit
// disparaître » · « mais il doit se souvenir si la personne a laissé activé ou désactivé […] pour la
// session en cours ; quand l'application se ferme ou qu'il y a eu une connexion ou une déconnexion,
// remettre l'œil par défaut, pour que le mot de passe soit toujours caché par défaut ».
//
// ⭐ LA MÉMOIRE DE LA SESSION TIENT DANS CETTE SEULE VARIABLE, et c'est voulu : elle vit le temps
// d'un CHARGEMENT DE PAGE. Se connecter, se déconnecter, revenir à l'écran de connexion, fermer
// l'application : chacun de ces gestes recharge une page, donc la remet à `false` tout seul — le mot
// de passe est masqué par défaut sans qu'aucun code n'ait à le décider. Pendant qu'on reste sur le
// même écran, en revanche, elle se souvient : effacer son mot de passe et le retaper ne redemande
// pas de rouvrir l'œil.
// ⛔ PAS DE `localStorage` ICI, ET SURTOUT PAS : un mot de passe affiché en clair au prochain
// lancement, par-dessus l'épaule de n'importe qui, serait exactement le contraire de ce qu'il demande.
let _gfOeilOuvert = false;
function gfOeilMotDePasse(racine) {
  const zone = racine || document;
  const champs = zone.querySelectorAll('input[type="password"]:not([data-oeil])');
  champs.forEach((champ) => {
    champ.setAttribute("data-oeil", "1");
    const boite = document.createElement("span");
    boite.className = "mdp-boite";
    champ.parentNode.insertBefore(boite, champ);
    boite.appendChild(champ);
    const b = document.createElement("button");
    b.type = "button";
    b.className = "mdp-oeil";
    b.setAttribute("aria-label", "Afficher le mot de passe");
    b.setAttribute("aria-pressed", "false");
    b.tabIndex = -1;   // il ne s'intercale pas entre le champ et le bouton « Se connecter »
    const OEIL = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/></svg>';
    const BARRE = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12s3.6-7 10-7c2 0 3.7.7 5.1 1.6M22 12s-3.6 7-10 7c-2 0-3.7-.7-5.1-1.6"/><path d="M9.9 9.9a3 3 0 0 0 4.2 4.2"/><path d="M3 3l18 18"/></svg>';
    b.innerHTML = OEIL;
    b.hidden = true;   // il n'est pas là tant qu'on n'a rien tapé — sa demande

    /**
     * ⛔⛔⛔ ON NE DONNE **JAMAIS** LE FOCUS À CE CHAMP. C'est la troisième écriture de ces lignes, et
     * les deux premières ont chacune produit un défaut chez lui (2.32.35, ses mots du 2026-09-20 :
     * « il ne faut pas qu'en cliquant sur l'œil ça ouvre le clavier, faire ça partout, corrige,
     * mais si le clavier est déjà ouvert, alors ça change rien »).
     *
     * 🔴 L'HISTOIRE, PARCE QU'ELLE EXPLIQUE LA RÈGLE :
     *   ① v1 — l'appui sur l'œil sortait le focus du champ, et Android refermait le clavier.
     *   ② v2 — on remettait le focus à chaque clic. Le clavier ne se fermait plus… il S'OUVRAIT,
     *      parce que `focus()` sur un champ de saisie EST le geste qui ouvre le clavier d'Android.
     *   ③ v3 — on ne remettait le focus que si le champ l'avait déjà. Toujours faux, et c'est LE
     *      piège : sur Android, **on peut refermer le clavier sans retirer le focus** (le bouton
     *      retour, la flèche du clavier). Le champ reste alors `document.activeElement` avec le
     *      clavier fermé — et `focus()` le fait ressurgir. `activeElement` renseigne sur le FOCUS,
     *      jamais sur le CLAVIER : aucune page web ne peut savoir si le clavier est affiché.
     *
     * ✅ PUISQU'ON NE PEUT PAS LE SAVOIR, ON NE PARIE PAS : on ne fait rien qui puisse l'ouvrir.
     *   · `pointerdown`/`mousedown` annulés (plus bas) : le focus ne PART pas, il n'y a donc rien à
     *     lui rendre — le clavier ouvert le reste sans qu'on le touche ;
     *   · le curseur, lui, se repose avec `setSelectionRange`, qui ne donne pas le focus et n'ouvre
     *     aucun clavier ;
     *   · `focus()` n'est appelé QUE dans le cas résiduel où le changement de `type` a lui-même
     *     retiré le focus À UN CHAMP QUI L'AVAIT — on rend ce qu'on vient de casser, on n'en crée
     *     jamais. Si le champ ne l'avait pas, on n'y touche sous aucun prétexte.
     * ⚠️ `selectionStart` LÈVE sur un champ `type="password"` dans certains navigateurs : d'où le
     * `try`. Le repli (fin du texte) est le bon comportement par défaut.
     */
    const poser = (visible, garderLeFocus) => {
      // ⛔ ON NE TOUCHE À RIEN SI RIEN NE CHANGE, et ce n'est pas une optimisation : `suivre()` tourne
      // à CHAQUE frappe. Réécrire le `type` à chaque lettre remettrait le curseur en place cinquante
      // fois par mot de passe — et sur Android, chaque changement de type est une chance de plus de
      // voir le clavier se refermer, c'est-à-dire le défaut qu'on est en train de corriger.
      if ((champ.type === "text") === !!visible) return;
      // ⛔⛔⛔ TROIS LIGNES, ET RIEN D'AUTRE. C'est tout ce que fait ce bouton, et c'est la
      // QUATRIÈME écriture : les trois précédentes touchaient au focus ou au curseur, et chacune a
      // rouvert ou refermé son clavier. Il n'y a plus un seul appel capable de réveiller le clavier
      // d'Android dans cette fonction — ni `focus()`, ni `setSelectionRange()`, ni `blur()`.
      // 🔴 CE QUI RESTAIT, ET QUI N'ÉTAIT PAS `focus()` : `setSelectionRange()` sur un champ qui a la
      // main est lu par Android comme « on édite ici » et fait remonter le clavier, tout comme
      // `focus()`. J'avais ajouté cette remise en place du curseur de moi-même, le 19, en supposant
      // qu'il fallait la réparer — IL NE ME L'AVAIT JAMAIS DEMANDÉ. Un correctif inventé pour un
      // problème supposé a produit le vrai problème, trois fois de suite.
      // ⚠️ CONSÉQUENCE ASSUMÉE : après un appui sur l'œil, le curseur peut se retrouver à la fin du
      // texte. C'est là qu'on continue de taper, et c'est le prix d'un clavier qui ne bouge jamais.
      // S'il signale que le curseur saute au MILIEU d'une saisie, il faudra une autre voie — mais
      // pas celle-ci, et pas sans qu'il l'ait demandé.
      champ.type = visible ? "text" : "password";
      b.innerHTML = visible ? BARRE : OEIL;
      b.setAttribute("aria-pressed", visible ? "true" : "false");
      b.setAttribute("aria-label", visible ? "Masquer le mot de passe" : "Afficher le mot de passe");
      void garderLeFocus;   // gardé pour la signature ; plus personne n'a le droit de s'en servir
    };

    // ⛔⛔ `pointerdown` PLUS `mousedown`, ET LES DEUX SONT ANNULÉS. C'est l'appui — pas le clic —
    // qui retire le focus du champ, et c'est ce retrait qui referme le clavier sur Android. En
    // l'empêchant, le champ ne perd jamais la main : le clavier ne bouge pas de l'écran.
    // ⚠️ `mousedown` en plus de `pointerdown` : les WebView anciennes n'émettent que le second, et
    // une seule des deux lignes laisse le défaut revenir sur la moitié des téléphones.
    // ⛔ ET RIEN D'AUTRE ICI. Le second paramètre de `poser()` ne veut plus dire « reprends le
    // focus » mais « repose le curseur » : le focus, lui, n'est rendu que si le changement de
    // `type` vient de le retirer (voir le long commentaire de `poser`). Toute tentative de décider
    // ici, à partir de `document.activeElement`, serait un pari sur l'état du clavier — et ce pari
    // a déjà été perdu deux fois : sur Android, le clavier se ferme sans que le focus parte.
    const retenir = (e) => { e.preventDefault(); };
    b.addEventListener("pointerdown", retenir);
    b.addEventListener("mousedown", retenir);
    b.addEventListener("click", (e) => {
      e.preventDefault();
      _gfOeilOuvert = champ.type === "password";
      poser(_gfOeilOuvert, true);
    });

    // ⭐ L'ŒIL SUIT LE CHAMP : absent tant qu'il est vide, présent dès la première frappe.
    // ⛔ ET UN CHAMP VIDE REPASSE EN MASQUÉ SANS OUBLIER LE CHOIX : `_gfOeilOuvert` n'est pas touché
    // ici. Effacer son mot de passe et le retaper retrouve donc l'état qu'on avait laissé, ce qui
    // est exactement sa demande — alors qu'afficher du vide en clair n'aurait aucun sens.
    const suivre = () => {
      const rempli = !!champ.value;
      b.hidden = !rempli;
      poser(rempli && _gfOeilOuvert, false);
    };
    champ.addEventListener("input", suivre);
    suivre();   // un champ pré-rempli (gestionnaire de mots de passe) a son œil tout de suite
    boite.appendChild(b);
  });
  return champs.length;
}
// ⚠️ LES DEUX MOMENTS, ET PAS UN : `config.js` est chargé en tête de page (le script n'est pas
// `defer` partout), donc `document.readyState` peut être « loading » — les champs n'existent pas
// encore. Il peut aussi être déjà complet quand une page le charge en bas. On couvre les deux.
if (typeof document !== "undefined") {
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => gfOeilMotDePasse());
  else gfOeilMotDePasse();
}
